import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { api, API_BASE, getToken, formatApiErrorDetail } from "@/lib/api";
import { buildDocFilename } from "@/lib/filename";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CAEN_CODES } from "@/lib/caen";
import {
  ArrowLeft,
  FileText,
  ClipboardCheck,
  Plus,
  Download,
  Trash2,
  Phone,
  Mail,
  MapPin,
  Building2,
  User,
  Eye,
  ExternalLink,
  X,
  Pencil,
  Send,
  Activity,
  Smartphone,
  MessageCircle,
  Copy,
  QrCode,
  PenTool,
  Sparkles,
  Loader2,
  Ban,
} from "lucide-react";
import PdfViewer from "@/components/PdfViewer";
import { SignaturePad } from "@/components/SignaturePad";
import { useAuth } from "@/lib/auth";

export default function ClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [client, setClient] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [pvs, setPvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openContract, setOpenContract] = useState(false);
  const [openPV, setOpenPV] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [editingPV, setEditingPV] = useState(null);
  const [shareItem, setShareItem] = useState(null); // { kind: 'contract'|'pv', doc }
  const [signItem, setSignItem] = useState(null); // { kind: 'contract'|'pv', doc }
  const signPadRef = useRef(null);
  const [shareLink, setShareLink] = useState("");
  const [sharePdfBlob, setSharePdfBlob] = useState(null);
  const [preview, setPreview] = useState(null); // { url, title, downloadName }
  const [preselectedContract, setPreselectedContract] = useState(null); // Pre-link a PV to a specific contract
  const [openPortalShare, setOpenPortalShare] = useState(false);
  const [autoGenContract, setAutoGenContract] = useState(null); // Contract for which auto-PV dialog is open
  const [draftInfo, setDraftInfo] = useState({ contract: null, pv: null }); // { ts, form.numar? }
  const [openClientEdit, setOpenClientEdit] = useState(false);
  const [clientForm, setClientForm] = useState(null);
  const [clientSaving, setClientSaving] = useState(false);
  const [clientAnafLoading, setClientAnafLoading] = useState(false);

  // Check for saved drafts in localStorage (from accidentally-closed dialogs)
  const checkDrafts = () => {
    if (!id) return;
    let contract = null, pv = null;
    try {
      const rawC = localStorage.getItem(`contract_draft_${id}`);
      if (rawC) {
        const d = JSON.parse(rawC);
        contract = { ts: d.ts || Date.now(), numar: d.form?.numar || "fără număr" };
      }
    } catch {}
    try {
      const rawP = localStorage.getItem(`pv_draft_${id}`);
      if (rawP) {
        const d = JSON.parse(rawP);
        pv = { ts: d.ts || Date.now(), numar: d.form?.numar || "fără număr" };
      }
    } catch {}
    setDraftInfo({ contract, pv });
  };

  useEffect(() => { checkDrafts(); /* eslint-disable-next-line */ }, [id]);
  // Re-check drafts when dialogs close
  useEffect(() => { if (!openContract && !openPV) checkDrafts(); /* eslint-disable-next-line */ }, [openContract, openPV]);

  const deleteDraft = (kind) => {
    try { localStorage.removeItem(`${kind}_draft_${id}`); } catch {}
    checkDrafts();
    toast.success("Draft șters");
  };

  const load = async () => {
    setLoading(true);
    try {
      const [c, cs, ps] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get(`/clients/${id}/contracts`),
        api.get(`/clients/${id}/pvs`),
      ]);
      setClient(c.data);
      setContracts(cs.data);
      setPvs(ps.data);
    } catch (e) {
      toast.error("Client inexistent");
      nav("/clienti");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Auto-open Contract/PV dialog from ?action=new-contract | new-pv (TopBar quick actions)
  useEffect(() => {
    const action = searchParams.get("action");
    if (action === "new-contract") {
      setOpenContract(true);
      searchParams.delete("action");
      setSearchParams(searchParams, { replace: true });
    } else if (action === "new-pv") {
      setOpenPV(true);
      searchParams.delete("action");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line
  }, [searchParams]);

  // Cleanup preview blob URL when modal closes or component unmounts
  useEffect(() => {
    return () => {
      if (preview?.url) URL.revokeObjectURL(preview.url);
    };
  }, [preview]);

  const fetchPdfBlob = async (kind, itemId) => {
    const res = await fetch(`${API_BASE}/${kind}/${itemId}/pdf`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!res.ok) throw new Error("PDF eroare");
    const raw = await res.blob();
    // Force application/pdf MIME so <object> / iframe recognize it correctly
    return new Blob([raw], { type: "application/pdf" });
  };

  const viewPdf = async (kind, itemId, numar, isDDD) => {
    try {
      const blob = await fetchPdfBlob(kind, itemId);
      const url = URL.createObjectURL(blob);
      const prefix = kind === "contracts" ? (isDDD ? "Contract DDD" : "Contract") : (isDDD ? "PV DDD" : "PV");
      setPreview({
        url,
        title: `${prefix} Nr. ${numar}`,
        downloadName: buildDocFilename(kind, client, numar),
      });
    } catch {
      toast.error("Nu s-a putut deschide documentul");
    }
  };

  const downloadPdf = async (kind, itemId, numar) => {
    try {
      const blob = await fetchPdfBlob(kind, itemId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = buildDocFilename(kind, client, numar);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Nu s-a putut descărca PDF-ul");
    }
  };

  const downloadFromPreview = () => {
    if (!preview) return;
    const a = document.createElement("a");
    a.href = preview.url;
    a.download = preview.downloadName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const deleteContract = async (cid) => {
    if (!confirm("Ștergi acest contract?")) return;
    await api.delete(`/contracts/${cid}`);
    toast.success("Contract șters");
    load();
  };
  const cancelContract = async (c) => {
    if (c.is_canceled) {
      if (!confirm("Reactivezi acest contract? Va putea din nou fi folosit.")) return;
      try {
        await api.patch(`/contracts/${c.id}/uncancel`);
        toast.success("Contract reactivat");
        load();
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Nu am putut reactiva contractul");
      }
      return;
    }
    const motiv = prompt("Anulezi contractul " + (c.numar || "") + "?\n\nMotivul anulării (opțional):", "");
    if (motiv === null) return; // user pressed Cancel in prompt
    try {
      await api.patch(`/contracts/${c.id}/cancel`, { motiv });
      toast.success("Contract anulat");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Nu am putut anula contractul");
    }
  };
  const deletePv = async (pid) => {
    if (!confirm("Ștergi acest PV?")) return;
    await api.delete(`/pvs/${pid}`);
    toast.success("PV șters");
    load();
  };

  const openShare = async (kind, doc) => {
    setShareItem({ kind, doc });
    setShareLink("");
    setSharePdfBlob(null);
    try {
      // Generate the public link + fetch the PDF file in parallel
      const path = kind === "contract" ? `/contracts/${doc.id}/share-link` : `/pvs/${doc.id}/share-link`;
      const [{ data }, pdfBlob] = await Promise.all([
        api.post(path),
        fetchPdfBlob(kind === "contract" ? "contracts" : "pvs", doc.id).catch(() => null),
      ]);
      const backend = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      setShareLink(`${backend}${data.path}`);
      setSharePdfBlob(pdfBlob);
    } catch (e) {
      toast.error("Nu am putut genera link-ul");
      setShareItem(null);
    }
  };

  const nativeShareFile = async () => {
    if (!sharePdfBlob || !shareItem) return;
    const kind = shareItem.kind === "contract" ? "Contract" : "PV";
    const filename = buildDocFilename(shareItem.kind, client, shareItem.doc?.numar || "doc");
    const file = new File([sharePdfBlob], filename, { type: "application/pdf" });
    const shareData = {
      title: `${kind} ${shareItem.doc?.numar}`,
      text: `${kind} ${shareItem.doc?.numar} — ${client?.denumire || ""}`,
      files: [file],
    };
    if (navigator.canShare && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
        toast.success("Trimis cu succes");
      } catch (e) {
        if (e.name !== "AbortError") toast.error("Trimitere anulată");
      }
    } else {
      toast.error("Trimiterea directă a fișierului nu e suportată aici. Folosește butoanele WhatsApp / Email cu link.");
    }
  };

  const downloadSharePdf = () => {
    if (!sharePdfBlob || !shareItem) return;
    const url = URL.createObjectURL(sharePdfBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = buildDocFilename(shareItem.kind, client, shareItem.doc?.numar || "doc");
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // Normalize a phone number for wa.me — keep digits only, prepend country code if missing
  const waPhone = (raw) => {
    if (!raw) return "";
    let d = String(raw).replace(/\D/g, "");
    if (!d) return "";
    if (d.startsWith("00")) d = d.slice(2);
    if (d.startsWith("0")) d = "40" + d.slice(1); // România
    return d;
  };

  if (loading || !client) {
    return <div className="text-muted-foreground">Se încarcă...</div>;
  }

  return (
    <div className="space-y-8">
      {/* Draft banner — apare când există PV sau Contract nesalvat */}
      {(draftInfo.contract || draftInfo.pv) && (
        <div className="rounded-md border-2 border-dashed border-amber-400 bg-amber-50 p-4" data-testid="draft-banner">
          <div className="flex items-start gap-3">
            <span className="text-xl">⏸️</span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-amber-900">Ai draft-uri nesalvate</p>
              <p className="text-xs text-amber-800 mt-0.5">Continuă de unde ai rămas — draft-urile sunt salvate local pe acest device.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {draftInfo.contract && (
                  <div className="flex items-center gap-1 bg-white border border-amber-300 rounded-md px-2 py-1 text-xs">
                    <FileText className="w-3.5 h-3.5 text-amber-700" />
                    <span className="font-mono">Contract {draftInfo.contract.numar}</span>
                    <button
                      type="button"
                      onClick={() => { setEditingContract(null); setOpenContract(true); }}
                      className="ml-1 text-emerald-700 hover:underline font-semibold"
                      data-testid="draft-continue-contract"
                    >Continuă →</button>
                    <button
                      type="button"
                      onClick={() => deleteDraft("contract")}
                      className="ml-1 text-red-600 hover:text-red-800"
                      aria-label="Șterge draft"
                      data-testid="draft-delete-contract"
                    >×</button>
                  </div>
                )}
                {draftInfo.pv && (
                  <div className="flex items-center gap-1 bg-white border border-amber-300 rounded-md px-2 py-1 text-xs">
                    <ClipboardCheck className="w-3.5 h-3.5 text-amber-700" />
                    <span className="font-mono">PV {draftInfo.pv.numar}</span>
                    <button
                      type="button"
                      onClick={() => { setEditingPV(null); setPreselectedContract(null); setOpenPV(true); }}
                      className="ml-1 text-emerald-700 hover:underline font-semibold"
                      data-testid="draft-continue-pv"
                    >Continuă →</button>
                    <button
                      type="button"
                      onClick={() => deleteDraft("pv")}
                      className="ml-1 text-red-600 hover:text-red-800"
                      aria-label="Șterge draft"
                      data-testid="draft-delete-pv"
                    >×</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div>
        <Link to="/clienti" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-gold-deep transition-colors" data-testid="back-to-clients">
          <ArrowLeft className="w-4 h-4" /> Toți clienții
        </Link>
      </div>

      <header className="bg-white border border-border rounded-md p-6 sm:p-8">
        <div>
          <p className="label-eyebrow">CUI {client.cui}{client.nr_reg_com ? ` • ${client.nr_reg_com}` : ""}</p>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-2 break-words" data-testid="client-name">
            {client.denumire}
          </h1>
          <div className="flex flex-wrap gap-2 mt-4">
            <Button
              onClick={() => setOpenContract(true)}
              className="btn-gold hover:btn-gold"
              data-testid="add-contract-btn"
            >
              <FileText className="w-4 h-4 mr-2" /> Creează contract
            </Button>
            {contracts.length > 0 && (
              <Button
                onClick={() => setOpenPV(true)}
                variant="outline"
                className="border-gold text-gold-deep hover:bg-gold hover:text-white transition-colors"
                data-testid="add-pv-btn"
              >
                <ClipboardCheck className="w-4 h-4 mr-2" /> Creează PV
              </Button>
            )}
            <Button
              onClick={() => setOpenPortalShare(true)}
              variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-900 transition-colors"
              data-testid="send-portal-access-btn"
            >
              <Smartphone className="w-4 h-4 mr-2" /> Trimite acces portal
            </Button>
            <Button
              onClick={() => {
                setClientForm({
                  cui: client.cui || "",
                  denumire: client.denumire || "",
                  adresa: client.adresa || "",
                  judet: client.judet || "",
                  localitate: client.localitate || "",
                  nr_reg_com: client.nr_reg_com || "",
                  telefon: client.telefon || "",
                  email: client.email || "",
                  persoana_contact: client.persoana_contact || "",
                  observatii: client.observatii || "",
                });
                setOpenClientEdit(true);
              }}
              variant="outline"
              className="border-slate-300 hover:bg-slate-50"
              data-testid="edit-client-btn"
            >
              <Pencil className="w-4 h-4 mr-2" /> Editează client
            </Button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-4 text-sm">
          <InfoRow icon={MapPin} label="Adresă" value={[client.adresa, client.localitate, client.judet].filter(Boolean).join(", ") || "—"} />
          <InfoRow icon={Phone} label="Telefon" value={client.telefon || "—"} />
          <InfoRow icon={Mail} label="Email" value={client.email || "—"} />
          <InfoRow icon={User} label="Contact" value={client.persoana_contact || "—"} />
        </div>

        {/* Statistici client */}
        {(() => {
          const today = new Date();
          const activeContracts = contracts.filter((c) => {
            if (!c.data || !c.durata_luni) return false;
            const start = new Date(c.data);
            const end = new Date(start); end.setMonth(end.getMonth() + Number(c.durata_luni));
            return end >= today;
          });
          const expiredContracts = contracts.length - activeContracts.length;
          const valoare = contracts.reduce((s, c) => s + (parseFloat(c.valoare) || 0), 0);
          const consum = pvs.reduce((s, p) => s + ((p.substante || []).reduce((ss, x) => ss + (parseFloat(x.consum_total || x.consum_concentrata) || 0), 0)), 0);
          const sortedPvs = [...pvs].sort((a, b) => (b.data || "").localeCompare(a.data || ""));
          const lastPv = sortedPvs[0];
          const clientSince = client.created_at ? new Date(client.created_at) : null;
          const monthsSince = clientSince ? Math.max(1, Math.round((today - clientSince) / (30 * 24 * 3600 * 1000))) : null;
          return (
            <div className="mt-6 border-t border-border pt-5">
              <p className="label-eyebrow mb-3 flex items-center gap-1.5">
                <Activity className="w-3.5 h-3.5 text-gold-deep" /> Statistici client
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="client-stats">
                <StatMini label="Contracte total" value={contracts.length} sub={`${activeContracts.length} active • ${expiredContracts} expirate`} color="text-amber-700" bg="bg-amber-50" />
                <StatMini label="PV-uri total" value={pvs.length} sub={lastPv?.data ? `Ultimul: ${new Date(lastPv.data).toLocaleDateString("ro-RO")}` : "Fără PV încă"} color="text-emerald-700" bg="bg-emerald-50" />
                <StatMini label="Consum substanțe" value={`${consum.toFixed(2)}`} sub="kg / l cumulat" color="text-blue-700" bg="bg-blue-50" />
                <StatMini label="Valoare contracte" value={`${valoare.toLocaleString("ro-RO")}`} sub="RON cumulat" color="text-purple-700" bg="bg-purple-50" />
                <StatMini label="Client de la" value={clientSince ? clientSince.toLocaleDateString("ro-RO") : "—"} sub={monthsSince ? `${monthsSince} luni` : ""} color="text-slate-700" bg="bg-slate-50" />
                <StatMini label="Serviciu preferat" value={(() => {
                  const svc = {};
                  pvs.forEach((p) => (p.servicii || []).forEach((s) => { svc[s] = (svc[s] || 0) + 1; }));
                  const top = Object.entries(svc).sort((a, b) => b[1] - a[1])[0];
                  return top ? top[0] : "—";
                })()} sub={pvs.length ? `Din ${pvs.length} PV-uri` : ""} color="text-gold-deep" bg="bg-gold-soft/40" />
              </div>
            </div>
          );
        })()}
      </header>

      <Tabs defaultValue="contracts">
        <TabsList className="bg-white border border-border h-auto p-1">
          <TabsTrigger value="contracts" className="data-[state=active]:bg-[hsl(var(--gold))] data-[state=active]:text-white px-4 py-2" data-testid="tab-contracts">
            <FileText className="w-4 h-4 mr-2" /> Contracte ({contracts.length})
          </TabsTrigger>
          <TabsTrigger value="pvs" className="data-[state=active]:bg-[hsl(var(--gold))] data-[state=active]:text-white px-4 py-2" data-testid="tab-pvs">
            <ClipboardCheck className="w-4 h-4 mr-2" /> Procese Verbale ({pvs.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="contracts" className="mt-6 space-y-3">
          {contracts.length === 0 ? (
            <EmptyBlock icon={FileText} text="Niciun contract înregistrat" cta="Creează primul contract" onClick={() => setOpenContract(true)} />
          ) : contracts.map((c) => {
            const contractPvs = pvs.filter((p) => p.contract_id === c.id);
            return (
            <ItemRow
              key={c.id}
              title={`${c.template === "ddd" ? "Contract DDD" : "Contract"} Nr. ${c.numar}`}
              subtitle={c.template === "ddd" ? (c.locatie ? `📍 ${c.locatie}` : (c.obiect || "Servicii DDD")) : (c.locatie ? `📍 ${c.locatie}` : c.obiect)}
              meta={[
                new Date(c.data).toLocaleDateString("ro-RO"),
                c.template === "ddd" && c.durata_luni ? `${c.durata_luni} luni` : null,
                c.valoare ? `${Number(c.valoare).toLocaleString("ro-RO")} ${c.moneda || "RON"}` : null,
              ].filter(Boolean).join(" • ")}
              onView={() => viewPdf("contracts", c.id, c.numar, c.template === "ddd")}
              onDownload={() => downloadPdf("contracts", c.id, c.numar)}
              onEdit={() => { setEditingContract(c); setOpenContract(true); }}
              onDuplicate={async () => {
                try {
                  const { data } = await api.get(`/contracts/next-number?template=${c.template || 'generic'}`);
                  const clone = { ...c, id: undefined, _duplicate: true, numar: data.numar, data: new Date().toISOString().slice(0, 10), created_at: undefined };
                  setEditingContract(clone);
                  setOpenContract(true);
                  toast.success(`Duplicat cu numărul ${data.numar}`);
                } catch (e) {
                  toast.error("Nu am putut duplica contractul");
                }
              }}
              onShare={() => openShare("contract", c)}
              onSign={() => setSignItem({ kind: "contract", doc: c })}
              isSigned={!!c.beneficiary_signature_path}
              onCancel={() => cancelContract(c)}
              isCanceled={!!c.is_canceled}
              onAddPv={() => { setPreselectedContract(c); setEditingPV(null); setOpenPV(true); }}
              onAutoGen={c.template === "ddd" ? () => setAutoGenContract(c) : null}
              pvList={contractPvs}
              onViewPv={(p) => viewPdf("pvs", p.id, p.numar, true)}
              testid={`contract-row-${c.id}`}
            />
            );
          })}
        </TabsContent>

        <TabsContent value="pvs" className="mt-6 space-y-3">
          {contracts.length === 0 ? (
            <div className="text-center py-12 bg-white border border-border rounded-md">
              <ClipboardCheck className="w-10 h-10 mx-auto text-muted-foreground" />
              <p className="mt-3 font-medium text-foreground">Nu se poate crea un PV fără contract</p>
              <p className="mt-1 text-sm text-muted-foreground">Creează întâi un contract pentru acest client.</p>
              <Button
                onClick={() => setOpenContract(true)}
                className="btn-gold hover:btn-gold mt-4"
              >
                <FileText className="w-4 h-4 mr-2" /> Creează contract
              </Button>
            </div>
          ) : pvs.length === 0 ? (
            <EmptyBlock icon={ClipboardCheck} text="Niciun proces verbal înregistrat" cta="Creează primul PV" onClick={() => setOpenPV(true)} />
          ) : pvs.map((p) => (
            <ItemRow
              key={p.id}
              title={`${p.template === "ddd" ? "PV DDD" : "PV"} Nr. ${p.numar}`}
              subtitle={p.template === "ddd"
                ? (p.servicii && p.servicii.length ? p.servicii.join(" • ") : (p.locatie || p.descriere || "Intervenție DDD"))
                : p.descriere}
              meta={[
                p.template === "ddd" ? "Intervenție DDD" : p.tip,
                new Date(p.data).toLocaleDateString("ro-RO"),
                p.template === "ddd" && p.suprafata_mp ? `${p.suprafata_mp} mp` : null,
              ].filter(Boolean).join(" • ")}
              onView={() => viewPdf("pvs", p.id, p.numar, p.template === "ddd")}
              onDownload={() => downloadPdf("pvs", p.id, p.numar)}
              onEdit={() => { setEditingPV(p); setOpenPV(true); }}
              onShare={() => openShare("pv", p)}
              onSign={() => setSignItem({ kind: "pv", doc: p })}
              isSigned={!!p.beneficiary_signature_path}
              onDelete={() => deletePv(p.id)}
              testid={`pv-row-${p.id}`}
            />
          ))}
        </TabsContent>
      </Tabs>

      <ContractDialog
        open={openContract}
        setOpen={(v) => { setOpenContract(v); if (!v) setEditingContract(null); }}
        clientId={id}
        client={client}
        editing={editingContract}
        onDone={load}
      />
      <PVDialog
        open={openPV}
        setOpen={(v) => { setOpenPV(v); if (!v) { setEditingPV(null); setPreselectedContract(null); } }}
        clientId={id}
        editing={editingPV}
        preselectedContract={preselectedContract}
        onDone={load}
      />

      {/* Share Dialog (Email + WhatsApp) */}
      <Dialog open={!!shareItem} onOpenChange={(v) => { if (!v) { setShareItem(null); setShareLink(""); setSharePdfBlob(null); } }}>
        <DialogContent className="w-[95vw] max-w-md max-h-[85dvh] xl:max-h-[92dvh] overflow-y-auto" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              Trimite {shareItem?.kind === "contract" ? "Contract" : "PV"} {shareItem?.doc?.numar}
            </DialogTitle>
            <DialogDescription>
              {shareItem?.kind === "contract"
                ? "Trimite PDF-ul contractului direct pe telefon/email — fără link public."
                : "PDF-ul este atașat sau accesibil via link public 30 zile."}
            </DialogDescription>
          </DialogHeader>
          {shareLink ? (
            <div className="space-y-3">
              {shareItem?.kind === "pv" && (
                <div className="bg-slate-50 border border-border rounded-md p-3 break-all text-xs font-mono">
                  {shareLink}
                </div>
              )}
              {/* Direct share — attaches the actual PDF file (mobile / PWA) */}
              {typeof navigator !== "undefined" && navigator.canShare && sharePdfBlob && (
                <Button
                  type="button"
                  onClick={nativeShareFile}
                  className="w-full h-12 btn-gold hover:btn-gold text-base"
                  data-testid="share-native-file"
                >
                  <Send className="w-4 h-4 mr-2" /> Trimite PDF direct (WhatsApp / Email / ...)
                </Button>
              )}
              {shareItem?.kind === "pv" && (
                <div className="grid grid-cols-2 gap-2">
                  <a
                    href={`https://wa.me/${waPhone(client?.telefon)}?text=${encodeURIComponent(`Bună ziua ${client?.persoana_contact || client?.denumire || ""}, vă transmit PV-ul ${shareItem?.doc?.numar} pentru ${client?.denumire || ""} (PDF): ${shareLink}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-12 flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-md transition-colors"
                    data-testid="share-whatsapp"
                  >
                    <ExternalLink className="w-4 h-4" /> WhatsApp
                    {client?.telefon && <span className="text-[10px] opacity-80">→ {client.telefon}</span>}
                  </a>
                  <a
                    href={`mailto:${client?.email || ""}?subject=${encodeURIComponent(`PV ${shareItem?.doc?.numar} — IMAGGIO 2020 TIM`)}&body=${encodeURIComponent(`Bună ziua,\n\nVă transmit procesul verbal ${shareItem?.doc?.numar} pentru firma ${client?.denumire || ""} (PDF descărcabil):\n\n${shareLink}\n\nCu stimă,\nIMAGGIO 2020 TIM SRL`)}`}
                    className="h-12 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-md transition-colors"
                    data-testid="share-email"
                  >
                    <ExternalLink className="w-4 h-4" /> Email
                  </a>
                </div>
              )}
              <div className={shareItem?.kind === "pv" ? "grid grid-cols-2 gap-2" : ""}>
                <Button
                  type="button"
                  variant="outline"
                  onClick={downloadSharePdf}
                  disabled={!sharePdfBlob}
                  className="w-full"
                  data-testid="share-download-pdf"
                >
                  <Download className="w-4 h-4 mr-2" /> Descarcă PDF
                </Button>
                {shareItem?.kind === "pv" && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { navigator.clipboard?.writeText(shareLink); toast.success("Link copiat"); }}
                    className="w-full"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" /> Copiază link
                  </Button>
                )}
              </div>
              {shareItem?.kind === "pv" && (
                <Button
                  type="button"
                  onClick={async () => {
                    const t = toast.loading("Se pregătește ZIP-ul cu PV + Avize...");
                    try {
                      const resp = await api.get(`/pvs/${shareItem.doc.id}/bundle`, { responseType: "blob" });
                      const blob = new Blob([resp.data], { type: "application/zip" });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = buildDocFilename("pv", client, `${shareItem.doc.numar || "doc"}_cu_avize`, "zip");
                      document.body.appendChild(a); a.click(); a.remove();
                      URL.revokeObjectURL(url);
                      toast.dismiss(t);
                      toast.success("ZIP descărcat: PV + toate Avizele Bio + Fișele SSA");
                    } catch (e) {
                      toast.dismiss(t);
                      toast.error("Eroare la generarea ZIP");
                    }
                  }}
                  className="w-full mt-2 btn-gold"
                  data-testid="share-download-bundle-zip"
                >
                  <Download className="w-4 h-4 mr-2" /> Descarcă PV + Avize (ZIP)
                </Button>
              )}
              {!client?.telefon && (
                <p className="text-[11px] text-muted-foreground text-center italic">
                  Adaugă telefon la client ca WhatsApp să deschidă direct conversația cu el.
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">Se generează link-ul și se pregătește PDF-ul...</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Signature Pad — Beneficiar semnează pe tabletă */}
      <Dialog open={!!signItem} onOpenChange={(v) => { if (!v) { setSignItem(null); } }}>        <DialogContent className="w-[95vw] max-w-2xl max-h-[85dvh] xl:max-h-[92dvh] overflow-y-auto" data-testid="signature-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <PenTool className="w-6 h-6 text-amber-600" />
              Semnătură beneficiar
            </DialogTitle>
            <DialogDescription>
              {signItem?.kind === "contract" ? "Contract" : "PV"} {signItem?.doc?.numar} · {client?.denumire}
              <br />Semnează cu degetul sau stylus-ul. Se salvează pe document și apare automat în toate PDF-urile descărcate/trimise ulterior.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {signItem && (
              <SignaturePad
                ref={signPadRef}
                width={640}
                height={280}
              />
            )}
            {signItem?.doc?.beneficiary_signature_path && (
              <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-2">
                Există deja o semnătură salvată. Dacă desenezi una nouă și apeși „Salvează", o va înlocui.
              </p>
            )}
          </div>
          <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:justify-between">
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => signPadRef.current?.clear()}
                data-testid="signature-clear"
              >
                Curăță
              </Button>
              {signItem?.doc?.beneficiary_signature_path && (
                <Button
                  type="button"
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={async () => {
                    if (!signItem) return;
                    try {
                      const base = signItem.kind === "contract" ? "contracts" : "pvs";
                      await api.delete(`/${base}/${signItem.doc.id}/beneficiary-signature`);
                      toast.success("Semnătură ștearsă");
                      setSignItem(null);
                      load();
                    } catch (e) {
                      toast.error("Nu am putut șterge semnătura");
                    }
                  }}
                  data-testid="signature-delete"
                >
                  Șterge semnătura
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setSignItem(null); }}
                data-testid="signature-cancel"
              >
                Anulează
              </Button>
              <Button
                type="button"
                className="btn-gold hover:btn-gold"
                onClick={async () => {
                  if (!signItem || !signPadRef.current) return;
                  if (signPadRef.current.isEmpty()) {
                    toast.error("Semnează întâi cu degetul/stylus-ul");
                    return;
                  }
                  const dataUrl = signPadRef.current.getDataUrl();
                  try {
                    const base = signItem.kind === "contract" ? "contracts" : "pvs";
                    await api.patch(`/${base}/${signItem.doc.id}/beneficiary-signature`, { signature: dataUrl });
                    toast.success("Semnătură salvată");
                    setSignItem(null);
                    load();
                  } catch (e) {
                    toast.error(formatApiErrorDetail(e) || "Nu am putut salva semnătura");
                  }
                }}
                data-testid="signature-save"
              >
                Salvează semnătura
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Editează client */}
      <Dialog open={openClientEdit} onOpenChange={(v) => { if (!v) { setOpenClientEdit(false); setClientForm(null); } }}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[85dvh] xl:max-h-[92dvh] overflow-y-auto p-4 xl:p-6" data-testid="edit-client-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl xl:text-2xl">Modifică client</DialogTitle>
            <DialogDescription>
              Actualizează datele firmei. Se propagă automat în Beneficiar la toate contractele și PV-urile.
            </DialogDescription>
          </DialogHeader>
          {clientForm && (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!clientForm.cui.trim() || !clientForm.denumire.trim()) {
                  toast.error("CUI și denumirea sunt obligatorii");
                  return;
                }
                setClientSaving(true);
                try {
                  await api.patch(`/clients/${id}`, clientForm);
                  toast.success("Client actualizat");
                  setOpenClientEdit(false);
                  setClientForm(null);
                  load();
                } catch (err) {
                  toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Eroare salvare");
                } finally {
                  setClientSaving(false);
                }
              }}
              className="space-y-3 xl:space-y-4"
            >
              <div className="grid sm:grid-cols-[1fr_auto] gap-2 items-end">
                <div>
                  <Label className="label-eyebrow">CUI *</Label>
                  <Input
                    value={clientForm.cui}
                    onChange={(e) => setClientForm({ ...clientForm, cui: e.target.value })}
                    className="mt-1.5 h-10 xl:h-11"
                    required
                    data-testid="edit-client-cui"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 xl:h-11 border-gold text-gold-deep hover:bg-gold hover:text-white transition-colors"
                  disabled={clientAnafLoading || !clientForm.cui}
                  onClick={async () => {
                    setClientAnafLoading(true);
                    try {
                      const { data } = await api.get(`/anaf/${clientForm.cui.trim()}`);
                      setClientForm((f) => ({
                        ...f,
                        cui: data.cui || f.cui,
                        denumire: data.denumire || f.denumire,
                        adresa: data.adresa || f.adresa,
                        judet: data.judet || f.judet,
                        localitate: data.localitate || f.localitate,
                        nr_reg_com: data.nr_reg_com || f.nr_reg_com,
                        telefon: data.telefon || f.telefon,
                      }));
                      toast.success("Date preluate de la ANAF");
                    } catch (err) {
                      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Eroare ANAF");
                    } finally {
                      setClientAnafLoading(false);
                    }
                  }}
                  data-testid="edit-client-anaf-btn"
                >
                  {clientAnafLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  Reîmprospătează ANAF
                </Button>
              </div>
              <div className="grid sm:grid-cols-2 gap-3 xl:gap-4">
                <div className="sm:col-span-2">
                  <Label className="label-eyebrow">Denumire firmă *</Label>
                  <Input value={clientForm.denumire} onChange={(e) => setClientForm({ ...clientForm, denumire: e.target.value })} className="mt-1.5 h-10 xl:h-11" required data-testid="edit-client-denumire" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="label-eyebrow">Adresă</Label>
                  <Input value={clientForm.adresa} onChange={(e) => setClientForm({ ...clientForm, adresa: e.target.value })} className="mt-1.5 h-10 xl:h-11" />
                </div>
                <div>
                  <Label className="label-eyebrow">Județ</Label>
                  <Input value={clientForm.judet} onChange={(e) => setClientForm({ ...clientForm, judet: e.target.value })} className="mt-1.5 h-10 xl:h-11" />
                </div>
                <div>
                  <Label className="label-eyebrow">Localitate</Label>
                  <Input value={clientForm.localitate} onChange={(e) => setClientForm({ ...clientForm, localitate: e.target.value })} className="mt-1.5 h-10 xl:h-11" />
                </div>
                <div>
                  <Label className="label-eyebrow">Nr. Reg. Com.</Label>
                  <Input value={clientForm.nr_reg_com} onChange={(e) => setClientForm({ ...clientForm, nr_reg_com: e.target.value })} className="mt-1.5 h-10 xl:h-11" />
                </div>
                <div>
                  <Label className="label-eyebrow">Telefon</Label>
                  <Input value={clientForm.telefon} onChange={(e) => setClientForm({ ...clientForm, telefon: e.target.value })} className="mt-1.5 h-10 xl:h-11" />
                </div>
                <div>
                  <Label className="label-eyebrow">Email</Label>
                  <Input value={clientForm.email} onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })} className="mt-1.5 h-10 xl:h-11" />
                </div>
                <div>
                  <Label className="label-eyebrow">Persoană contact</Label>
                  <Input value={clientForm.persoana_contact} onChange={(e) => setClientForm({ ...clientForm, persoana_contact: e.target.value })} className="mt-1.5 h-10 xl:h-11" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="label-eyebrow">Observații</Label>
                  <Textarea value={clientForm.observatii} onChange={(e) => setClientForm({ ...clientForm, observatii: e.target.value })} className="mt-1.5" rows={2} />
                </div>
              </div>
              <DialogFooter className="flex flex-col sm:flex-row gap-2">
                <Button type="button" variant="outline" onClick={() => { setOpenClientEdit(false); setClientForm(null); }} data-testid="edit-client-cancel">
                  Anulează
                </Button>
                <Button type="submit" className="btn-gold hover:btn-gold" disabled={clientSaving} data-testid="edit-client-save">
                  {clientSaving ? "Se salvează..." : "Salvează modificările"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Trimite Acces Portal Client (SMS / WhatsApp / Copy) */}
      <Dialog open={openPortalShare} onOpenChange={setOpenPortalShare}>
        <DialogContent className="w-[95vw] max-w-lg max-h-[85dvh] xl:max-h-[92dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Smartphone className="w-6 h-6 text-emerald-600" />
              Trimite acces portal client
            </DialogTitle>
            <DialogDescription>
              Clientul se poate autentifica pe telefon cu telefonul lui și CUI-ul firmei ca să vadă contractele și PV-urile.
            </DialogDescription>
          </DialogHeader>

          {(() => {
            const portalUrl = `${window.location.origin}/client-portal`;
            const contact = client?.persoana_contact || client?.denumire || "";
            const msg = `Bună ziua${contact ? " " + contact : ""},

Vă trimit accesul la portalul IMAGGIO 2020 TIM SRL — puteți vedea și descărca contractele și procesele verbale ale firmei ${client?.denumire || ""} direct de pe telefon.

🔗 Link portal: ${portalUrl}
📱 Utilizator (telefon): ${client?.telefon || "—"}
🔑 Parolă (CUI firmă): ${client?.cui || "—"}

Pentru instalare ca aplicație pe telefon:
• Android: deschideți linkul → meniul cu 3 puncte → „Instalare aplicație"
• iPhone: deschideți în Safari → butonul Share ⇪ → „Adaugă pe ecranul de start"

Cu stimă,
IMAGGIO 2020 TIM SRL`;

            const waLink = `https://wa.me/${waPhone(client?.telefon)}?text=${encodeURIComponent(msg)}`;
            const smsLink = `sms:${client?.telefon || ""}?body=${encodeURIComponent(msg)}`;
            const mailLink = `mailto:${client?.email || ""}?subject=${encodeURIComponent("Acces portal IMAGGIO 2020 TIM — " + (client?.denumire || ""))}&body=${encodeURIComponent(msg)}`;

            return (
              <div className="space-y-4">
                {/* Credentials preview */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-emerald-800 font-semibold text-xs uppercase tracking-wider">Link</span><a href={portalUrl} target="_blank" rel="noopener noreferrer" className="text-emerald-900 font-mono text-xs truncate hover:underline">{portalUrl}</a></div>
                  <div className="flex justify-between"><span className="text-emerald-800 font-semibold text-xs uppercase tracking-wider">Telefon</span><span className="text-emerald-900 font-mono">{client?.telefon || "—"}</span></div>
                  <div className="flex justify-between"><span className="text-emerald-800 font-semibold text-xs uppercase tracking-wider">CUI</span><span className="text-emerald-900 font-mono">{client?.cui || "—"}</span></div>
                </div>

                {/* Message preview */}
                <div>
                  <Label className="label-eyebrow mb-2 block">Mesaj generat (edițabil în client de mesagerie)</Label>
                  <Textarea readOnly value={msg} className="min-h-[180px] font-mono text-xs" data-testid="portal-share-msg" />
                </div>

                {/* Warnings */}
                {!client?.telefon && (
                  <p className="text-xs bg-amber-50 border border-amber-200 rounded-md p-2 text-amber-900">
                    ⚠ Clientul nu are telefon salvat — nu se poate autentifica pe portal până nu adaugi telefonul pe fișă.
                  </p>
                )}

                {/* Action buttons */}
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    asChild
                    disabled={!client?.telefon}
                    className="h-11 bg-[#25D366] hover:bg-[#1EA952] text-white"
                    data-testid="portal-share-whatsapp"
                  >
                    <a href={waLink} target="_blank" rel="noopener noreferrer">
                      <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
                    </a>
                  </Button>
                  <Button
                    type="button"
                    asChild
                    variant="outline"
                    disabled={!client?.telefon}
                    className="h-11 border-blue-300 text-blue-700 hover:bg-blue-50"
                    data-testid="portal-share-sms"
                  >
                    <a href={smsLink}>
                      <Send className="w-4 h-4 mr-2" /> SMS
                    </a>
                  </Button>
                  <Button
                    type="button"
                    asChild
                    variant="outline"
                    disabled={!client?.email}
                    className="h-11"
                    data-testid="portal-share-email"
                  >
                    <a href={mailLink}>
                      <Mail className="w-4 h-4 mr-2" /> Email
                    </a>
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11"
                    onClick={() => {
                      navigator.clipboard?.writeText(msg);
                      toast.success("Mesajul a fost copiat");
                    }}
                    data-testid="portal-share-copy"
                  >
                    <Copy className="w-4 h-4 mr-2" /> Copiază mesajul
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* PDF Preview Modal */}
      <Dialog open={!!preview} onOpenChange={(v) => { if (!v) closePreview(); }}>
        <DialogContent className="w-[95vw] max-w-5xl h-[85dvh] xl:h-[92dvh] p-0 flex flex-col gap-0 overflow-hidden">
          <div className="px-6 py-4 border-b border-border flex items-center justify-between shrink-0">
            <div>
              <p className="label-eyebrow">Vizualizare document</p>
              <p className="font-display font-bold text-lg">{preview?.title}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => preview && window.open(preview.url, "_blank", "noopener")}
                data-testid="preview-open-new-tab"
              >
                <ExternalLink className="w-4 h-4 mr-2" /> Tab nou
              </Button>
              <Button
                size="sm"
                onClick={downloadFromPreview}
                className="btn-gold hover:btn-gold"
                data-testid="preview-download"
              >
                <Download className="w-4 h-4 mr-2" /> Descarcă
              </Button>
              <Button size="sm" variant="ghost" onClick={closePreview} className="hover:bg-slate-100">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="flex-1 bg-slate-200 min-h-0">
            {preview?.url && (
              <PdfViewer file={preview.url} className="h-full" />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Auto-generate monthly PVs — choose which months and use first manual PV as template */}
      <AutoPVDialog
        contract={autoGenContract}
        pvs={pvs}
        onClose={() => setAutoGenContract(null)}
        onDone={() => { setAutoGenContract(null); load(); }}
      />
    </div>
  );
}
function StatMini({ label, value, sub, color = "text-slate-700", bg = "bg-slate-50" }) {
  return (
    <div className={`border border-border rounded-md px-3 py-2.5 ${bg}`}>
      <p className="label-eyebrow leading-tight">{label}</p>
      <p className={`text-xl font-black font-display mt-1 ${color}`}>{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground mt-0.5 uppercase tracking-wider">{sub}</p>}
    </div>
  );
}



function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-8 h-8 border border-border rounded-md flex items-center justify-center shrink-0 bg-slate-50">
        <Icon className="w-4 h-4 text-slate-700" />
      </div>
      <div className="min-w-0">
        <p className="label-eyebrow">{label}</p>
        <p className="text-sm mt-1 break-words">{value}</p>
      </div>
    </div>
  );
}

function EmptyBlock({ icon: Icon, text, cta, onClick }) {
  return (
    <div className="border border-dashed border-border rounded-md p-12 text-center bg-white">
      <Icon className="w-8 h-8 mx-auto text-muted-foreground" />
      <p className="mt-3 font-medium">{text}</p>
      <Button onClick={onClick} className="mt-4 btn-gold hover:btn-gold">
        <Plus className="w-4 h-4 mr-2" /> {cta}
      </Button>
    </div>
  );
}

function ItemRow({ title, subtitle, meta, onView, onDownload, onEdit, onShare, onSign, isSigned, onCancel, isCanceled, onDelete, onAddPv, onAutoGen, onDuplicate, pvList, onViewPv, testid }) {
  return (
    <div className={`bg-white border rounded-md p-5 flex flex-col gap-4 lift-hover ${isCanceled ? "border-red-200 bg-red-50/40 opacity-80" : "border-border"}`} data-testid={testid}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1 min-w-0">
          <button
            type="button"
            onClick={onView}
            className={`font-display font-bold text-lg text-left hover:text-gold-deep transition-colors ${isCanceled ? "line-through text-slate-500" : ""}`}
            data-testid={`${testid}-title`}
          >
            {title}
            {isCanceled && (
              <span className="ml-2 inline-flex items-center gap-1 align-middle text-[10px] font-bold uppercase tracking-wider bg-red-600 text-white px-2 py-0.5 rounded no-underline">
                <Ban className="w-3 h-3" /> Anulat
              </span>
            )}
          </button>
          <p className="text-sm text-muted-foreground truncate">{subtitle}</p>
          <p className="label-eyebrow mt-1">{meta}</p>
        </div>
        <div className="flex gap-2 shrink-0 flex-wrap">
          <Button variant="outline" size="sm" onClick={onView} data-testid={`${testid}-view`} className="border-gold text-gold-deep hover:bg-gold hover:text-white transition-colors">
            <Eye className="w-4 h-4 mr-2" /> Vezi
          </Button>
          {onAddPv && (
            <Button variant="outline" size="sm" onClick={onAddPv} data-testid={`${testid}-add-pv`} className="border-emerald-400 text-emerald-700 hover:bg-emerald-50">
              <ClipboardCheck className="w-4 h-4 mr-2" /> + PV
            </Button>
          )}
          {onAutoGen && (
            <Button variant="outline" size="sm" onClick={onAutoGen} data-testid={`${testid}-auto-gen`} className="border-purple-400 text-purple-700 hover:bg-purple-50" title="Generează automat 12 PV-uri lunare (nu scad din stoc)">
              <Activity className="w-4 h-4 mr-2" /> Auto PV 12 luni
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onDownload} data-testid={`${testid}-pdf`}>
            <Download className="w-4 h-4 mr-2" /> PDF
          </Button>
          {onShare && (
            <Button variant="outline" size="sm" onClick={onShare} data-testid={`${testid}-share`} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
              <Send className="w-4 h-4 mr-2" /> Trimite
            </Button>
          )}
          {onSign && (
            <Button
              variant="outline"
              size="sm"
              onClick={onSign}
              data-testid={`${testid}-sign`}
              className={isSigned
                ? "border-emerald-500 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                : "border-amber-400 text-amber-800 hover:bg-amber-50"}
              title={isSigned ? "Re-semnează beneficiar" : "Semnează beneficiar pe tabletă"}
            >
              <PenTool className="w-4 h-4 mr-2" />
              {isSigned ? "Semnat ✓" : "Semnează"}
            </Button>
          )}
          {onEdit && (
            <Button variant="outline" size="sm" onClick={onEdit} data-testid={`${testid}-edit`} className="border-slate-300 hover:bg-slate-50">
              <Pencil className="w-4 h-4" />
            </Button>
          )}
          {onDuplicate && (
            <Button variant="outline" size="sm" onClick={onDuplicate} data-testid={`${testid}-duplicate`} className="border-blue-300 text-blue-700 hover:bg-blue-50" title="Duplică cu număr nou">
              <Copy className="w-4 h-4" />
            </Button>
          )}
          {onCancel && (
            <Button
              variant="outline"
              size="sm"
              onClick={onCancel}
              data-testid={`${testid}-cancel`}
              className={isCanceled
                ? "border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                : "border-orange-300 text-orange-700 hover:bg-orange-50"}
              title={isCanceled ? "Reactivează contractul" : "Anulează contractul"}
            >
              <Ban className="w-4 h-4 mr-2" />
              {isCanceled ? "Reactivează" : "Anulează"}
            </Button>
          )}
          {onDelete && (
            <Button variant="outline" size="sm" onClick={onDelete} className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200">
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
      {pvList && pvList.length > 0 && (
        <div className="border-t border-border pt-3">
          <p className="label-eyebrow mb-2 flex items-center gap-2">
            <ClipboardCheck className="w-3.5 h-3.5" />
            Procese Verbale asociate ({pvList.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {pvList.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => onViewPv && onViewPv(p)}
                data-testid={`${testid}-pv-chip-${p.id}`}
                className="inline-flex items-center gap-1.5 text-xs font-mono border border-gold/40 bg-gold-soft/60 hover:bg-gold-soft text-gold-deep rounded-md px-2.5 py-1 transition-colors"
                title={`Vezi PV ${p.numar}`}
              >
                <Eye className="w-3 h-3" />
                {p.numar}
                <span className="text-[10px] text-muted-foreground">
                  {p.data ? new Date(p.data).toLocaleDateString("ro-RO", { day: "2-digit", month: "2-digit" }) : ""}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const DDD_SERVICES = ["DEZINSECȚIE", "DEZINFECȚIE", "DERATIZARE"];
const RO_MONTHS_SHORT = ["Ian", "Feb", "Mar", "Apr", "Mai", "Iun", "Iul", "Aug", "Sep", "Oct", "Noi", "Dec"];

function ContractDialog({ open, setOpen, clientId, client, onDone, editing }) {
  const isEdit = !!editing && !editing._duplicate;
  const [template, setTemplate] = useState("ddd");
  const [form, setForm] = useState({
    numar: "", data: new Date().toISOString().slice(0, 10),
    obiect: "", valoare: "", moneda: "RON", durata: "", observatii: "",
    // Contract type: contract | prestari_servicii (used for both templates)
    tip: "contract",
    // Frequency of service execution
    frecventa_luni: 0,
    // DDD fields
    locatie: "",
    durata_luni: "0",
    termen_plata_zile: "0",
    garantie_zile: "0",
    pret_dezinsectie: "",
    pret_deratizare: "",
    pret_dezinfectie: "",
    pret_statie_ext: "",
    pret_statie_int: "",
    pret_armare: "",
    suprafata_mp_interior: "",
    suprafata_mp_exterior: "",
    coduri_caen: [], // Array of {cod, denumire}
    persoana_contact: "",
    telefon_contact: "",
    email_contact: "",
    tip_obiectiv: "", // Tipul obiectivului (dropdown)
    obiectiv_sub: "", // Subcategorie tip obiectiv
    obiectiv_detalii: [], // Din ce este alcătuit punctul de lucru — array of strings
    obiectiv_detaliu_extra: "", // Subcategorie liberă
  });
  const [caenSearch, setCaenSearch] = useState("");
  const [caenManualCod, setCaenManualCod] = useState("");
  const [caenManualDen, setCaenManualDen] = useState("");
  const [serviciiPrestari, setServiciiPrestari] = useState([]);
  const [plan, setPlan] = useState({});
  const [saving, setSaving] = useState(false);
  const [autoNumber, setAutoNumber] = useState(true);

  // ── Auto-save draft to localStorage (only for NEW contracts) ──
  const contractDraftKey = clientId ? `contract_draft_${clientId}` : null;
  const [contractDraftRestored, setContractDraftRestored] = useState(false);

  useEffect(() => {
    if (!open || isEdit || !contractDraftKey) return;
    try {
      const raw = localStorage.getItem(contractDraftKey);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d && (d.form || d.serviciiPrestari || d.plan)) {
        setTimeout(() => {
          if (d.form) setForm((f) => ({ ...f, ...d.form }));
          if (d.serviciiPrestari) setServiciiPrestari(d.serviciiPrestari);
          if (d.plan) setPlan(d.plan);
          if (d.template) setTemplate(d.template);
          setContractDraftRestored(true);
          toast.info("Draft contract restaurat.", { duration: 3500 });
        }, 300);
      }
    } catch {}
    // eslint-disable-next-line
  }, [open, isEdit, contractDraftKey]);

  useEffect(() => {
    if (!open || isEdit || !contractDraftKey) return;
    const t = setTimeout(() => {
      try { localStorage.setItem(contractDraftKey, JSON.stringify({ form, serviciiPrestari, plan, template, ts: Date.now() })); } catch {}
    }, 500);
    return () => clearTimeout(t);
  }, [form, serviciiPrestari, plan, template, open, isEdit, contractDraftKey]);

  const clearContractDraft = () => {
    if (contractDraftKey) { try { localStorage.removeItem(contractDraftKey); } catch {} }
    setContractDraftRestored(false);
  };

  // Fetch next auto number whenever template changes (if auto mode on)
  useEffect(() => {
    if (!open || !autoNumber) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/contracts/next-number", { params: { template } });
        if (alive) setForm((f) => ({ ...f, numar: data.numar }));
      } catch {}
    })();
    return () => { alive = false; };
  }, [open, template, autoNumber]);

  useEffect(() => {
    if (open) {
      if (isEdit && editing) {
        // Pre-fill from the existing contract
        setForm({
          numar: editing.numar || "",
          data: editing.data || new Date().toISOString().slice(0, 10),
          obiect: editing.obiect || "",
          valoare: editing.valoare != null ? String(editing.valoare) : "",
          moneda: editing.moneda || "RON",
          durata: editing.durata || "",
          observatii: editing.observatii || "",
          tip: editing.tip || "contract",
          frecventa_luni: editing.frecventa_luni || 0,
          locatie: editing.locatie || "",
          durata_luni: editing.durata_luni != null ? String(editing.durata_luni) : "0",
          suprafata_mp_interior: editing.suprafata_mp_interior != null ? String(editing.suprafata_mp_interior) : "",
          suprafata_mp_exterior: editing.suprafata_mp_exterior != null ? String(editing.suprafata_mp_exterior) : "",
          coduri_caen: Array.isArray(editing.coduri_caen) ? editing.coduri_caen : [],
          persoana_contact: editing.persoana_contact || "",
          telefon_contact: editing.telefon_contact || "",
          email_contact: editing.email_contact || "",
          tip_obiectiv: editing.tip_obiectiv || "",
          obiectiv_sub: editing.obiectiv_sub || "",
          obiectiv_detalii: Array.isArray(editing.obiectiv_detalii) ? editing.obiectiv_detalii : [],
          obiectiv_detaliu_extra: editing.obiectiv_detaliu_extra || "",
          termen_plata_zile: editing.termen_plata_zile != null ? String(editing.termen_plata_zile) : "0",
          garantie_zile: editing.garantie_zile != null ? String(editing.garantie_zile) : "0",
          pret_dezinsectie: editing.pret_dezinsectie != null ? String(editing.pret_dezinsectie) : "",
          pret_deratizare: editing.pret_deratizare != null ? String(editing.pret_deratizare) : "",
          pret_dezinfectie: editing.pret_dezinfectie != null ? String(editing.pret_dezinfectie) : "",
          pret_statie_ext: editing.pret_statie_ext != null ? String(editing.pret_statie_ext) : "",
          pret_statie_int: editing.pret_statie_int != null ? String(editing.pret_statie_int) : "",
          pret_armare: editing.pret_armare != null ? String(editing.pret_armare) : "",
        });
        setServiciiPrestari(editing.servicii_prestari || []);
        setPlan(editing.plan_lunar || {});
        setTemplate(editing.template || "generic");
        setAutoNumber(false);
      } else {
        setForm((f) => ({
          ...f, numar: "", obiect: "", valoare: "", durata: "", observatii: "",
          locatie: "", durata_luni: "0", termen_plata_zile: "0", garantie_zile: "0",
          suprafata_mp_interior: "", suprafata_mp_exterior: "",
          coduri_caen: [],
          // Preia automat contactul din fișa clientului
          persoana_contact: client?.persoana_contact || "",
          telefon_contact: client?.telefon || "",
          email_contact: client?.email || "",
          tip_obiectiv: "", obiectiv_sub: "",
          obiectiv_detalii: [], obiectiv_detaliu_extra: "",
          pret_dezinsectie: "", pret_deratizare: "", pret_dezinfectie: "",
          pret_statie_ext: "", pret_statie_int: "", pret_armare: "",
          tip: "contract", frecventa_luni: 0,
        }));
        setServiciiPrestari([]);
        setPlan({});
        setTemplate("ddd");
        setAutoNumber(true);
      }
    }
  }, [open, isEdit, editing, client]);

  const togglePlan = (month, svc) => {
    setPlan((p) => {
      const key = String(month);
      const cur = new Set(p[key] || []);
      if (cur.has(svc)) cur.delete(svc); else cur.add(svc);
      const next = { ...p, [key]: Array.from(cur) };
      if (next[key].length === 0) delete next[key];
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (template === "generic") {
      if (!form.numar || !form.obiect || !form.data) {
        toast.error("Număr, dată și obiect sunt obligatorii");
        return;
      }
    } else {
      if (!form.numar || !form.data) {
        toast.error("Număr și dată sunt obligatorii");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        numar: form.numar,
        data: form.data,
        obiect: form.obiect || "Servicii DDD — dezinsecție, dezinfecție și deratizare",
        valoare: parseFloat(form.valoare) || 0,
        moneda: form.moneda,
        durata: form.durata,
        observatii: form.observatii,
        template,
      };
      if (template === "ddd") {
        Object.assign(payload, {
          locatie: form.locatie,
          durata_luni: form.durata_luni !== "" ? parseInt(form.durata_luni) : null,
          termen_plata_zile: form.termen_plata_zile !== "" ? parseInt(form.termen_plata_zile) : null,
          garantie_zile: form.garantie_zile !== "" ? parseInt(form.garantie_zile) : null,
          pret_dezinsectie: form.pret_dezinsectie ? parseFloat(form.pret_dezinsectie) : null,
          pret_deratizare: form.pret_deratizare ? parseFloat(form.pret_deratizare) : null,
          pret_dezinfectie: form.pret_dezinfectie ? parseFloat(form.pret_dezinfectie) : null,
          pret_statie_ext: form.pret_statie_ext ? parseFloat(form.pret_statie_ext) : null,
          pret_statie_int: form.pret_statie_int ? parseFloat(form.pret_statie_int) : null,
          pret_armare: form.pret_armare ? parseFloat(form.pret_armare) : null,
          suprafata_mp_interior: form.suprafata_mp_interior ? parseFloat(form.suprafata_mp_interior) : 0,
          suprafata_mp_exterior: form.suprafata_mp_exterior ? parseFloat(form.suprafata_mp_exterior) : 0,
          coduri_caen: form.coduri_caen || [],
          persoana_contact: form.persoana_contact || "",
          telefon_contact: form.telefon_contact || "",
          email_contact: form.email_contact || "",
          tip_obiectiv: form.tip_obiectiv || "",
          obiectiv_sub: form.obiectiv_sub || "",
          obiectiv_detalii: form.obiectiv_detalii || [],
          obiectiv_detaliu_extra: form.obiectiv_detaliu_extra || "",
          plan_lunar: plan,
        });
      }
      const finalPayload = {
        ...payload,
        tip: "contract",
        frecventa_luni: form.frecventa_luni || null,
        servicii_prestari: serviciiPrestari,
      };
      if (isEdit) {
        await api.patch(`/contracts/${editing.id}`, finalPayload);
        toast.success("Contract actualizat");
      } else {
        const { data: created } = await api.post(`/clients/${clientId}/contracts`, finalPayload);
        if (created?.numar && created.numar !== finalPayload.numar) {
          toast.success(`Contract adăugat cu nr. ${created.numar} (${finalPayload.numar} fusese deja folosit de alt utilizator)`);
        } else {
          toast.success("Contract adăugat");
        }
      }
      clearContractDraft();
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="w-[95vw] max-w-3xl max-h-[85dvh] xl:max-h-[92dvh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isEdit ? "Modifică contract" : "Contract nou"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          {/* Frecvență execuție (1 / 3 / 12 luni) */}
          <div>
            <Label className="label-eyebrow">Frecvență execuție</Label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {[1, 3, 12].map((n) => {
                const active = form.frecventa_luni === n;
                return (
                  <button
                    type="button"
                    key={n}
                    onClick={() => setForm({ ...form, frecventa_luni: active ? 0 : n })}
                    data-testid={`contract-freq-${n}`}
                    className={`text-sm font-semibold border rounded-md py-2.5 transition-colors ${
                      active ? "border-gold bg-gold-soft text-gold-deep" : "border-border bg-white hover:border-slate-400"
                    }`}
                  >
                    {active ? "☑ " : "☐ "} La {n} {n === 1 ? "lună" : "luni"}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Fixed to DDD template; generic template removed per user request */}

          {/* Common fields */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="flex items-center justify-between">
                <Label className="label-eyebrow">Număr contract *</Label>
                <button
                  type="button"
                  onClick={() => setAutoNumber((v) => !v)}
                  className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border transition-colors ${
                    autoNumber
                      ? "border-gold bg-gold-soft text-gold-deep"
                      : "border-border text-muted-foreground hover:border-slate-400"
                  }`}
                  data-testid="contract-auto-number-toggle"
                  title="Comută între numerotare automată și manuală"
                >
                  {autoNumber ? "AUTO" : "MANUAL"}
                </button>
              </div>
              <Input
                value={form.numar}
                onChange={(e) => { setAutoNumber(false); setForm({ ...form, numar: e.target.value }); }}
                className="mt-2 h-11 font-mono-crm"
                required
                data-testid="contract-numar-input"
              />
            </div>
            <div>
              <Label className="label-eyebrow">Data *</Label>
              <Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} className="mt-2 h-11" required data-testid="contract-data-input" />
            </div>
          </div>

          {template === "generic" ? (
            <>
              <div>
                <Label className="label-eyebrow">Obiect contract *</Label>
                <Textarea value={form.obiect} onChange={(e) => setForm({ ...form, obiect: e.target.value })} className="mt-2" rows={3} required data-testid="contract-obiect-input" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Label className="label-eyebrow">Valoare</Label>
                  <Input type="number" step="0.01" value={form.valoare} onChange={(e) => setForm({ ...form, valoare: e.target.value })} className="mt-2 h-11" />
                </div>
                <div>
                  <Label className="label-eyebrow">Monedă</Label>
                  <Select value={form.moneda} onValueChange={(v) => setForm({ ...form, moneda: v })}>
                    <SelectTrigger className="mt-2 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="RON">RON</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="label-eyebrow">Durată</Label>
                <Input value={form.durata} onChange={(e) => setForm({ ...form, durata: e.target.value })} className="mt-2 h-11" placeholder="ex: 12 luni, nedeterminat" />
              </div>
            </>
          ) : (
            <>
              <div>
                <Label className="label-eyebrow">Locația prestării <span className="text-red-500">*</span></Label>
                <Input value={form.locatie} onChange={(e) => setForm({ ...form, locatie: e.target.value })} className={`mt-2 h-11 ${!form.locatie ? "border-red-300 focus:border-red-500" : ""}`} placeholder="Adresa exactă unde se prestează serviciile" data-testid="contract-locatie-input" />
              </div>

              {/* Domeniu de activitate — coduri CAEN multi-select */}
              <div>
                <Label className="label-eyebrow">Domeniu de activitate (Coduri CAEN)</Label>
                {form.coduri_caen && form.coduri_caen.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {form.coduri_caen.map((c) => (
                      <span
                        key={c.cod}
                        className="inline-flex items-center gap-1 bg-gold-soft text-gold-deep text-xs px-2 py-1 rounded-full"
                        data-testid={`caen-selected-${c.cod}`}
                      >
                        <b>{c.cod}</b> — {c.denumire.length > 40 ? c.denumire.slice(0, 40) + "…" : c.denumire}
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, coduri_caen: form.coduri_caen.filter((x) => x.cod !== c.cod) })}
                          className="ml-1 hover:text-red-600"
                          aria-label="Elimină"
                        >×</button>
                      </span>
                    ))}
                  </div>
                )}
                <details className="mt-2 border border-input rounded-md bg-white" data-testid="contract-caen-details">
                  <summary className="cursor-pointer px-3 h-11 flex items-center justify-between text-sm select-none">
                    <span>{form.coduri_caen?.length ? `${form.coduri_caen.length} cod(uri) selectate — click pentru a adăuga` : "— Caută și bifează codul CAEN —"}</span>
                    <span className="text-slate-400">▾</span>
                  </summary>
                  <div className="border-t border-slate-200 p-2">
                    <Input
                      value={caenSearch}
                      onChange={(e) => setCaenSearch(e.target.value)}
                      placeholder="Caută după cod (ex: 5610) sau nume (ex: restaurant)..."
                      className="h-9 text-sm mb-2"
                      data-testid="caen-search-input"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="max-h-64 overflow-y-auto space-y-0.5">
                      {(() => {
                        const q = caenSearch.trim().toLowerCase();
                        const filtered = q
                          ? CAEN_CODES.filter((c) => c.cod.includes(q) || c.denumire.toLowerCase().includes(q))
                          : CAEN_CODES;
                        if (filtered.length === 0) return <p className="text-xs text-muted-foreground text-center py-4">Niciun rezultat pentru "{caenSearch}". Folosește „Adaugă cod manual" jos.</p>;
                        return filtered.map((c) => {
                          const active = form.coduri_caen?.some((x) => x.cod === c.cod);
                          return (
                            <label key={c.cod} className={`flex items-start gap-2 px-2 py-1.5 text-xs rounded cursor-pointer ${active ? "bg-gold-soft text-gold-deep" : "hover:bg-slate-50"}`}>
                              <input
                                type="checkbox"
                                checked={active}
                                onChange={() => {
                                  if (active) {
                                    setForm({ ...form, coduri_caen: form.coduri_caen.filter((x) => x.cod !== c.cod) });
                                  } else {
                                    setForm({ ...form, coduri_caen: [...(form.coduri_caen || []), c] });
                                  }
                                }}
                                data-testid={`caen-checkbox-${c.cod}`}
                                className="mt-0.5"
                              />
                              <span className="flex-1 min-w-0"><b>{c.cod}</b> — {c.denumire}</span>
                            </label>
                          );
                        });
                      })()}
                    </div>
                    {/* Manual CAEN entry — for codes not in the list */}
                    <div className="mt-3 pt-3 border-t border-dashed border-slate-200">
                      <p className="label-eyebrow text-[10px] mb-1.5">Nu găsești codul? Scrie-l manual:</p>
                      <div className="grid grid-cols-[90px_1fr_auto] gap-1.5">
                        <Input
                          value={caenManualCod}
                          onChange={(e) => setCaenManualCod(e.target.value.replace(/\D/g, "").slice(0, 4))}
                          placeholder="ex: 5610"
                          className="h-9 text-sm font-mono"
                          onClick={(e) => e.stopPropagation()}
                          data-testid="caen-manual-cod"
                        />
                        <Input
                          value={caenManualDen}
                          onChange={(e) => setCaenManualDen(e.target.value)}
                          placeholder="Denumire activitate"
                          className="h-9 text-sm"
                          onClick={(e) => e.stopPropagation()}
                          data-testid="caen-manual-denumire"
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-9 border-gold text-gold-deep hover:bg-gold hover:text-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            const cod = caenManualCod.trim();
                            const den = caenManualDen.trim();
                            if (!cod || !den) { toast.error("Completează codul ȘI denumirea"); return; }
                            if (form.coduri_caen?.some((x) => x.cod === cod)) {
                              toast.error(`Codul ${cod} e deja adăugat`);
                              return;
                            }
                            setForm({ ...form, coduri_caen: [...(form.coduri_caen || []), { cod, denumire: den }] });
                            setCaenManualCod("");
                            setCaenManualDen("");
                            toast.success(`Cod ${cod} adăugat`);
                          }}
                          data-testid="caen-manual-add"
                        >
                          + Adaugă
                        </Button>
                      </div>
                    </div>
                  </div>
                </details>
              </div>

              {/* Contact contract — persoană, telefon, email */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="label-eyebrow">Persoană de contact</Label>
                  <Input
                    value={form.persoana_contact || ""}
                    onChange={(e) => setForm({ ...form, persoana_contact: e.target.value })}
                    className="mt-2 h-11"
                    data-testid="contract-persoana-contact"
                  />
                </div>
                <div>
                  <Label className="label-eyebrow">Telefon contact</Label>
                  <Input
                    type="tel"
                    value={form.telefon_contact || ""}
                    onChange={(e) => setForm({ ...form, telefon_contact: e.target.value })}
                    className="mt-2 h-11"
                    data-testid="contract-telefon-contact"
                  />
                </div>
                <div>
                  <Label className="label-eyebrow">Email contact</Label>
                  <Input
                    value={form.email_contact || ""}
                    onChange={(e) => setForm({ ...form, email_contact: e.target.value })}
                    className="mt-2 h-11"
                    data-testid="contract-email-contact"
                  />
                </div>
              </div>

              {/* Tipul obiectivului — free text (scris de utilizator) */}
              <div>
                <Label className="label-eyebrow">Tipul obiectivului / Domeniul de activitate</Label>
                <Input
                  value={form.tip_obiectiv || ""}
                  onChange={(e) => setForm({ ...form, tip_obiectiv: e.target.value })}
                  placeholder="ex: Restaurant, Cabinet stomatologic, Fabrică de pâine, Hală depozitare..."
                  className="mt-2 h-11"
                  data-testid="contract-tip-obiectiv"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Scrie exact ce este acest client — se preia automat în PV-urile viitoare.</p>
              </div>

              {/* Din ce este alcătuit punctul de lucru — dropdown colapsat cu search + scroll */}
              <div>
                <Label className="label-eyebrow">Din ce este alcătuit punctul de lucru</Label>
                <p className="text-[10px] text-muted-foreground mt-1">Bifează secțiunile — se vor prelua automat în PV-uri (le poți debifă în PV).</p>
                {(form.obiectiv_detalii || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {form.obiectiv_detalii.map((d) => (
                      <span key={d} className="inline-flex items-center gap-1 bg-gold-soft text-gold-deep text-xs px-2 py-1 rounded-full" data-testid={`obiectiv-selected-${d}`}>
                        {d}
                        <button
                          type="button"
                          onClick={() => setForm((f) => ({ ...f, obiectiv_detalii: f.obiectiv_detalii.filter((x) => x !== d) }))}
                          className="ml-1 hover:text-red-600"
                          aria-label="Elimină"
                        >×</button>
                      </span>
                    ))}
                  </div>
                )}
                <details className="mt-2 border border-input rounded-md bg-white" data-testid="contract-obiectiv-details">
                  <summary className="cursor-pointer px-3 h-11 flex items-center justify-between text-sm select-none">
                    <span>{(form.obiectiv_detalii || []).length ? `${form.obiectiv_detalii.length} secțiune${form.obiectiv_detalii.length > 1 ? "i" : ""} — click pentru a adăuga` : "— Caută și bifează secțiunile —"}</span>
                    <span className="text-slate-400">▾</span>
                  </summary>
                  <div className="border-t border-slate-200 p-2">
                    <Input
                      value={form.obiectiv_search || ""}
                      onChange={(e) => setForm({ ...form, obiectiv_search: e.target.value })}
                      placeholder="Caută secțiune (ex: bucătărie, curte, hol)..."
                      className="h-9 text-sm mb-2"
                      data-testid="obiectiv-search-input"
                      onClick={(ev) => ev.stopPropagation()}
                    />
                    <div className="max-h-64 overflow-y-auto space-y-0.5">
                      {(() => {
                        const q = (form.obiectiv_search || "").trim().toLowerCase();
                        const filtered = q ? ALL_ROOM_TYPES.filter((r) => r.toLowerCase().includes(q)) : ALL_ROOM_TYPES;
                        if (filtered.length === 0) return <p className="text-xs text-muted-foreground text-center py-4">Niciun rezultat pentru "{form.obiectiv_search}".</p>;
                        return filtered.map((d) => {
                          const active = (form.obiectiv_detalii || []).includes(d);
                          return (
                            <label key={d} className={`flex items-center gap-2 px-2 py-1.5 text-xs rounded cursor-pointer ${active ? "bg-gold-soft text-gold-deep" : "hover:bg-slate-50"}`}>
                              <input
                                type="checkbox"
                                checked={active}
                                onChange={() => setForm((f) => ({
                                  ...f,
                                  obiectiv_detalii: active
                                    ? f.obiectiv_detalii.filter((x) => x !== d)
                                    : [...(f.obiectiv_detalii || []), d],
                                }))}
                                data-testid={`contract-obiectiv-detaliu-${d}`}
                              />
                              <span>{d}</span>
                            </label>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </details>
                <div className="mt-2">
                  <Label className="label-eyebrow text-xs">Adaugă subcategorie (text liber)</Label>
                  <Input
                    value={form.obiectiv_detaliu_extra || ""}
                    onChange={(e) => setForm({ ...form, obiectiv_detaliu_extra: e.target.value })}
                    className="mt-1.5 h-10"
                    placeholder="ex: Etaj 2 aripa dreapta, hol lângă intrare..."
                    data-testid="contract-obiectiv-detaliu-extra"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="label-eyebrow">Durată (luni) <span className="text-red-500">*</span></Label>
                  <Input type="number" min="0" value={form.durata_luni} onChange={(e) => setForm({ ...form, durata_luni: e.target.value })} className={`mt-2 h-11 ${!form.durata_luni || Number(form.durata_luni) <= 0 ? "border-red-300 focus:border-red-500" : ""}`} placeholder="0" />
                </div>
                <div>
                  <Label className="label-eyebrow">Termen plată (zile)</Label>
                  <Input type="number" min="0" value={form.termen_plata_zile} onChange={(e) => setForm({ ...form, termen_plata_zile: e.target.value })} className="mt-2 h-11" placeholder="0" />
                </div>
                <div>
                  <Label className="label-eyebrow">Garanție (zile)</Label>
                  <Input type="number" min="0" value={form.garantie_zile} onChange={(e) => setForm({ ...form, garantie_zile: e.target.value })} className="mt-2 h-11" placeholder="0" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 bg-gold-soft/30 border border-gold/40 rounded-md p-3">
                <div>
                  <Label className="label-eyebrow text-gold-deep">Suprafață Interior (mp²)</Label>
                  <Input type="number" step="0.1" min="0" value={form.suprafata_mp_interior} onChange={(e) => setForm({ ...form, suprafata_mp_interior: e.target.value })} className="mt-2 h-11 bg-white" placeholder="0" data-testid="contract-suprafata-interior" />
                </div>
                <div>
                  <Label className="label-eyebrow text-gold-deep">Suprafață Exterior (mp²)</Label>
                  <Input type="number" step="0.1" min="0" value={form.suprafata_mp_exterior} onChange={(e) => setForm({ ...form, suprafata_mp_exterior: e.target.value })} className="mt-2 h-11 bg-white" placeholder="0" data-testid="contract-suprafata-exterior" />
                </div>
                <p className="col-span-2 text-xs text-muted-foreground italic">
                  Suprafețele se moștenesc automat în PV-urile create/generate pe acest contract.
                </p>
              </div>

              <div>
                <Label className="label-eyebrow">Servicii incluse *</Label>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {[
                    { code: "DEZINSECTIE", label: "Dezinsecție" },
                    { code: "DERATIZARE",  label: "Deratizare"  },
                    { code: "DEZINFECTIE", label: "Dezinfecție" },
                  ].map((s) => {
                    const active = serviciiPrestari.includes(s.code);
                    return (
                      <button
                        type="button"
                        key={s.code}
                        onClick={() => {
                          setServiciiPrestari((cur) => {
                            const next = active ? cur.filter((x) => x !== s.code) : [...cur, s.code];
                            // Regenerate obiect text — but only if user hasn't manually customized it beyond the auto template
                            const labels = ["DEZINSECTIE", "DERATIZARE", "DEZINFECTIE"]
                              .filter((c) => next.includes(c))
                              .map((c) => ({ DEZINSECTIE: "Dezinsecție", DERATIZARE: "Deratizare", DEZINFECTIE: "Dezinfecție" }[c]));
                            let auto = "";
                            if (labels.length === 1) auto = `Servicii de ${labels[0]}`;
                            else if (labels.length === 2) auto = `Servicii de ${labels[0]} și ${labels[1]}`;
                            else if (labels.length === 3) auto = `Servicii de Dezinsecție, Deratizare și Dezinfecție`;
                            setForm((f) => ({ ...f, obiect: auto }));
                            return next;
                          });
                        }}
                        data-testid={`contract-serv-${s.code.toLowerCase()}`}
                        className={`text-sm font-semibold border rounded-md py-2.5 px-3 transition-colors flex items-center justify-center gap-2 ${
                          active ? "border-gold bg-gold-soft text-gold-deep" : "border-border bg-white hover:border-slate-400"
                        }`}
                      >
                        {active ? "☑" : "☐"} {s.label}
                      </button>
                    );
                  })}
                </div>
                {serviciiPrestari.length > 0 && (
                  <p className="text-[11px] italic text-emerald-700 mt-1.5">
                    ✓ Aceste servicii vor apărea în PDF-ul contractului și vor fi pre-bifate pe PV-uri.
                  </p>
                )}
              </div>

              <div>
                <Label className="label-eyebrow">Obiect contract (opțional)</Label>
                <Textarea value={form.obiect} onChange={(e) => setForm({ ...form, obiect: e.target.value })} className="mt-2" rows={2} placeholder="Se completează automat din bifele de mai sus. Poți edita liber." />
              </div>

              {/* Prețuri */}
              <div>
                <Label className="label-eyebrow">Prețuri (fără TVA)</Label>
                <div className="mt-2 border border-border rounded-md divide-y divide-border">
                  {[
                    { k: "pret_dezinsectie", label: "Dezinsecție", unit: "" },
                    { k: "pret_deratizare", label: "Deratizare", unit: "" },
                    { k: "pret_dezinfectie", label: "Dezinfecție", unit: "" },
                    { k: "pret_statie_ext", label: "Stație toxică exterioară", unit: "/ buc" },
                    { k: "pret_statie_int", label: "Stație toxică interioară", unit: "/ buc" },
                    { k: "pret_armare", label: "Armare stație toxică", unit: "/ buc" },
                  ].map((r) => (
                    <div key={r.k} className="grid grid-cols-[1fr_auto_auto] items-center gap-2 px-3 py-2">
                      <span className="text-sm">{r.label}</span>
                      <Input
                        type="number" step="0.01" min="0"
                        value={form[r.k]}
                        onChange={(e) => setForm({ ...form, [r.k]: e.target.value })}
                        className="h-9 w-32 text-right font-mono-crm"
                        placeholder="0.00"
                        data-testid={`ddd-${r.k}`}
                      />
                      <span className="text-xs text-muted-foreground w-16">{form.moneda} {r.unit}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <Label className="label-eyebrow">Monedă</Label>
                  <Select value={form.moneda} onValueChange={(v) => setForm({ ...form, moneda: v })}>
                    <SelectTrigger className="h-9 w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="RON">RON</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Plan lunar */}
              <div>
                <Label className="label-eyebrow">Planul intervențiilor pe 1 an</Label>
                <p className="text-xs text-muted-foreground mt-1">Bifează lunile în care se prestează fiecare serviciu.</p>
                <div className="mt-3 border border-border rounded-md overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-50">
                        <th className="text-left px-3 py-2 font-semibold">Serviciu</th>
                        {RO_MONTHS_SHORT.map((m, i) => (
                          <th key={m} className="px-1 py-2 font-semibold text-center w-10">{m}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {DDD_SERVICES.map((svc) => (
                        <tr key={svc} className="border-t border-border">
                          <td className="px-3 py-2 font-medium">{svc}</td>
                          {RO_MONTHS_SHORT.map((_, i) => {
                            const m = i + 1;
                            const active = (plan[String(m)] || []).includes(svc);
                            return (
                              <td key={m} className="text-center">
                                <button
                                  type="button"
                                  onClick={() => togglePlan(m, svc)}
                                  data-testid={`plan-${svc}-${m}`}
                                  className={`w-7 h-7 rounded-sm mx-auto flex items-center justify-center transition-colors ${
                                    active
                                      ? "bg-gold text-[hsl(var(--charcoal))] font-semibold"
                                      : "bg-white border border-border hover:border-gold"
                                  }`}
                                >
                                  {active ? "●" : ""}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          <div>
            <Label className="label-eyebrow">Observații</Label>
            <Textarea value={form.observatii} onChange={(e) => setForm({ ...form, observatii: e.target.value })} className="mt-2" rows={2} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => {
              // Flush draft immediately before closing (in case debounce hasn't fired yet)
              if (!isEdit && contractDraftKey) {
                try { localStorage.setItem(contractDraftKey, JSON.stringify({ form, serviciiPrestari, plan, template, ts: Date.now() })); } catch {}
              }
              setOpen(false);
            }}>Anulează</Button>
            {(() => {
              const missing = [];
              if (!form.persoana_contact) missing.push("Persoană de contact");
              if (!form.telefon_contact) missing.push("Telefon contact");
              if (template === "ddd") {
                if (!form.locatie) missing.push("Locația prestării");
                if (!form.durata_luni || Number(form.durata_luni) <= 0) missing.push("Durată (luni)");
                if (!serviciiPrestari || serviciiPrestari.length === 0) missing.push("Servicii incluse");
                if (!form.tip_obiectiv) missing.push("Tipul obiectivului");
                const iSup = Number(form.suprafata_mp_interior || 0);
                const eSup = Number(form.suprafata_mp_exterior || 0);
                if (iSup <= 0 && eSup <= 0) missing.push("Suprafață (interior sau exterior)");
              }
              const invalid = missing.length > 0;
              return (
                <>
                  {invalid && (
                    <div className="mr-auto self-center bg-red-50 border border-red-300 rounded-md px-3 py-2 max-w-[70%]" data-testid="contract-missing-fields">
                      <p className="text-xs font-bold text-red-800 mb-0.5">⚠ Lipsesc {missing.length} câmp{missing.length > 1 ? "uri" : ""}:</p>
                      <ul className="text-xs text-red-700 grid grid-cols-2 gap-x-3">
                        {missing.map((m) => <li key={m}>• {m}</li>)}
                      </ul>
                    </div>
                  )}
                  <Button
                    type="submit"
                    disabled={saving || invalid}
                    className="btn-gold hover:btn-gold"
                    data-testid="save-contract-btn"
                  >
                    {saving ? "Se salvează..." : "Salvează contract"}
                  </Button>
                </>
              );
            })()}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Detaliile fiecărui tip de obiectiv — flat, folosite ca „Din ce este alcătuit punctul de lucru".
// Fiecare tip conține și elementele comune (Bucătărie, Vestiar, Cameră tehnică).
const COMMON_DETAILS = ["Bucătărie", "Vestiar", "Cameră tehnică"];
const ALL_ROOM_TYPES = [
  "Bucătărie", "Vestiar", "Cameră tehnică",
  "Băi", "Camere", "Coridoare (spații comune)", "Terase", "Pod", "Beci / Pivniță", "Garaj", "Curte",
  "Sală de mese", "Sală de servire", "Bar", "Depozit alimente", "Depozit băuturi", "Vitrină", "Casă de marcat",
  "Grup sanitar", "Zonă preparare", "Sală de așteptare", "Cabinet consultație", "Sală de tratamente",
  "Recepție", "Depozit", "Zonă expediție", "Rampă", "Sală clase", "Curte școală", "Sală festivități",
];
const TIPURI_OBIECTIV = [
  {
    label: "Locuință",
    details: ["Băi", "Camere", "Coridoare (spații comune)", "Terase", "Pod", "Beci / Pivniță", "Garaj", "Curte", ...COMMON_DETAILS],
  },
  {
    label: "Bloc / ansamblu locuințe",
    details: ["Scară de bloc / spațiu comun", "Subsol", "Pod", "Ghenă", "Uscătorie", ...COMMON_DETAILS],
  },
  {
    label: "Grădină / spațiu verde",
    details: ["Spațiu verde", "Teren de fotbal", ...COMMON_DETAILS],
  },
  {
    label: "Spațiu comercial",
    details: ["Sală vânzare", "Depozit", "Casierie", "Rampă", ...COMMON_DETAILS],
  },
  {
    label: "Spațiu de producție",
    details: ["Hală producție", "Linie tehnologică", "Depozit MP", "Depozit PF", "Sală mese", ...COMMON_DETAILS],
  },
  {
    label: "Depozit",
    details: ["Depozit alimentar", "Depozit nealimentar", "Cameră frigorifică", "Freezer", "Rampă recepție", ...COMMON_DETAILS],
  },
  {
    label: "Bar / restaurant",
    details: ["Sală servire", "Bar", "Terasă", "Magazie", "Grup sanitar", "Cameră frigorifică", ...COMMON_DETAILS],
  },
  {
    label: "Cabinet medical / clinică",
    details: ["Sală tratament", "Sală așteptare", "Laborator", "Farmacie", "Grupuri sanitare", ...COMMON_DETAILS],
  },
  {
    label: "Brutărie / patiserie",
    details: ["Zonă producție", "Cuptor", "Depozit făină", "Vitrină expunere", ...COMMON_DETAILS],
  },
  { label: "Altele", details: [] },
];

const TIPURI_DAUNATORI = [
  "Gândaci de bucătărie",
  "Muște, țânțari",
  "Gândaci",
  "Furnici",
  "Căpușe",
  "Șoarece de casă",
  "Șobolan cenușiu",
  "Șobolan negru",
];

// Dăunători / agenți grupați pe tipul intervenției
const DAUNATORI_DEZINSECTIE = [
  "Gândaci de bucătărie", "Muște, țânțari", "Gândaci", "Furnici",
  "Căpușe", "Ploșnițe de pat", "Purici", "Molii",
  "Viespi", "Păianjeni", "Termite", "Larve",
];
const DAUNATORI_DERATIZARE = [
  "Șoarece de casă", "Șobolan cenușiu", "Șobolan negru", "Guzgani",
  "Cârtițe", "Popândău",
];
const DAUNATORI_DEZINFECTIE = [
  "Bacterii patogene", "Virusuri", "Fungi", "Spori",
  "Levuri", "Mucegai", "Alge", "Biofilm",
];
const DAUNATORI_BY_SERVICE = {
  "DEZINSECȚIE": DAUNATORI_DEZINSECTIE,
  "DERATIZARE": DAUNATORI_DERATIZARE,
  "DEZINFECȚIE": DAUNATORI_DEZINFECTIE,
};

const MODALITATI_INTERVENTIE = [
  "Pulverizare",
  "Gel insecticid",
  "Nebulizare la rece (ULV)",
  "Termonebulizare",
  "Momeală rodenticidă",
  "Stații intoxicare permanente",
  "Capcane monitorizare",
  "Aspersare / stropire",
  "Praf insecticid",
  "Fumigare",
  "Vapori / ceață",
  "Altele",
];

// Metode de aplicare — grupate pe serviciu (standard România)
const METODE_DEZINSECTIE = [
  "Pulverizare cu presiune",
  "Pulverizare manuală (pompă)",
  "Nebulizare la rece (ULV)",
  "Termonebulizare la cald",
  "Aerosolizare",
  "Gel insecticid",
  "Praf insecticid",
  "Momeală insecticidă",
  "Fumigare (pastile / lumânări)",
  "Aspersare / stropire",
  "Capcane feromonale",
  "Trape adezive",
  "Aplicare cu pensulă",
  "Injectare spații ascunse",
];

const METODE_DERATIZARE = [
  "Momeală pastă (parafinată)",
  "Momeală bloc parafinat",
  "Momeală cereale tratate",
  "Momeală grâu",
  "Praf de contact rodenticid",
  "Stații de intoxicare permanente",
  "Stații de intoxicare temporare",
  "Capcane mecanice (snap-trap)",
  "Capcane adezive (glue-trap)",
  "Capcane vii (cuști)",
  "Fumigare vizuini",
  "Sistem monitorizare (fără toxic)",
];

const METODE_DEZINFECTIE = [
  "Pulverizare soluție dezinfectantă",
  "Aspersare cu presiune",
  "Ștergere manuală suprafețe",
  "Nebulizare la rece (ULV)",
  "Termonebulizare",
  "Aerosolizare",
  "Vapori peroxid de hidrogen (H₂O₂)",
  "Ozon",
  "UV-C (radiație germicidă)",
  "Fumigare (pastile / soluție)",
  "Pulverizator electrostatic",
  "Imersie / scufundare",
];

const METODE_BY_SERVICE = {
  "DEZINSECȚIE": METODE_DEZINSECTIE,
  "DERATIZARE": METODE_DERATIZARE,
  "DEZINFECȚIE": METODE_DEZINFECTIE,
};

// Normalize service name to the canonical WITH-diacritics form used by
// METODE_BY_SERVICE / DAUNATORI_BY_SERVICE. Contracts sometimes save the
// no-diacritics variant ("DEZINSECTIE") which broke the PV dropdowns.
const normalizeService = (s) => {
  const up = String(s || "").toUpperCase().trim();
  if (up === "DEZINSECTIE" || up === "DEZINSECȚIE") return "DEZINSECȚIE";
  if (up === "DEZINFECTIE" || up === "DEZINFECȚIE") return "DEZINFECȚIE";
  if (up === "DERATIZARE") return "DERATIZARE";
  return up;
};
const normalizeServices = (arr) => {
  if (!Array.isArray(arr)) return [];
  const seen = new Set();
  const out = [];
  arr.forEach((raw) => {
    const s = normalizeService(raw);
    if (s && !seen.has(s)) { seen.add(s); out.push(s); }
  });
  return out;
};
const normalizeSvcKeyedMap = (obj) => {
  if (!obj || typeof obj !== "object") return {};
  const out = {};
  Object.keys(obj).forEach((k) => { out[normalizeService(k)] = obj[k]; });
  return out;
};

// Auto-fill helpers: get current HH:MM (from PC) and compute HH:MM + N minutes.
const nowHHMM = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};
const addMinutesToHHMM = (hhmm, mins) => {
  if (!hhmm || mins == null || mins === "") return "";
  const [h, m] = hhmm.split(":").map(Number);
  if (isNaN(h) || isNaN(m)) return "";
  const total = h * 60 + m + parseInt(mins);
  if (isNaN(total)) return "";
  const hh = String(((Math.floor(total / 60) % 24) + 24) % 24).padStart(2, "0");
  const mm = String(((total % 60) + 60) % 60).padStart(2, "0");
  return `${hh}:${mm}`;
};

// Controlled multi-check dropdown — a click-toggled panel with checkboxes.
// Replaces native <details>/<summary> which had issues inside Radix Dialog on some browsers.
function CheckMultiDropdown({ placeholder = "— Alege —", options = [], selected = [], onToggle, testId, itemTestIdPrefix }) {
  const [open, setOpen] = useState(false);
  const wrapRef = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (ev) => {
      if (wrapRef.current && !wrapRef.current.contains(ev.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  return (
    <div ref={wrapRef} className="relative border border-input rounded-md bg-white" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full cursor-pointer px-3 h-10 flex items-center justify-between text-sm select-none"
      >
        <span>{selected.length > 0 ? `${selected.length} selectat${selected.length > 1 ? "e" : ""}` : placeholder}</span>
        <span className="text-slate-400">{open ? "▴" : "▾"}</span>
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 z-30 bg-white border border-slate-200 rounded-md shadow-lg max-h-56 overflow-y-auto p-2 space-y-1">
          {options.length === 0 && <div className="text-xs text-muted-foreground px-2 py-2 italic">Nicio opțiune disponibilă.</div>}
          {options.map((o) => {
            const active = selected.includes(o);
            return (
              <label
                key={o}
                className={`flex items-center gap-2 px-2 py-1.5 text-[11px] rounded cursor-pointer ${active ? "bg-gold-soft text-gold-deep" : "hover:bg-slate-50"}`}
              >
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() => onToggle(o)}
                  data-testid={itemTestIdPrefix ? `${itemTestIdPrefix}-${o}` : undefined}
                />
                <span>{o}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

const SUBSTANTE_PRESET = {
  "SUPER KILLER 25T-EC": {
    substanta_activa: "Cipermetrină 25%, Tetrametrina 0,25%",
    compozitie: "Cipermetrină 25%, Tetrametrina 0,25%, Alcool izopropilic 10-60%, Glicerol formal, Polisorbat 80.",
    aviz: "Aviz nr. 5082BIO/18/12.24",
    mod_aplicare: "pulverizare pe suprafețe",
    timp_actiune: "60 min.",
  },
  "Tx3 GEL": {
    substanta_activa: "Imidacloprid 2,19%",
    compozitie: "Substanța activă: Imidacloprid 2,19%",
    aviz: "Autorizație RO/2019/0258/MRA/IT/2018/00526/AUT",
    mod_aplicare: "directă, sub formă de gel",
    timp_actiune: "180 min.",
  },
  "VARAT PASTA": {
    substanta_activa: "Brodifacoum 0,005%",
    compozitie: "Substanța activă: Brodifacoum 0,005%",
    aviz: "Autorizație RO/2019/0121/MRA/IT/2014/00179/AUT",
    mod_aplicare: "montare în stații intoxicare",
    timp_actiune: "4-10 zile de la ingerare",
  },
  "RATIDIF SENSITIVE PASTA FLUO": {
    substanta_activa: "Difenacoum 0,00029%",
    compozitie: "Substanța activă: Difenacoum 0,00029%",
    aviz: "Autorizație RO/2019/0121/MRA/IT/2014/00179/AUT",
    mod_aplicare: "montare în stații intoxicare",
    timp_actiune: "4-10 zile de la ingerare",
  },
};

function PVDialog({ open, setOpen, clientId, onDone, editing, preselectedContract }) {
  const isEdit = !!editing;
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [template, setTemplate] = useState("ddd");
  const [autoNumber, setAutoNumber] = useState(true);
  const [linkedContract, setLinkedContract] = useState(null);
  const [form, setForm] = useState({
    numar: "", data: new Date().toISOString().slice(0, 10),
    tip: "Servicii DDD", descriere: "", observatii: "",
    locatie: "", ora_start: "", ora_final: "",
    suprafata_mp: "0", metoda: "", tehnician: "", contract_numar: "",
    recomandari: "",
    tip_obiectiv: "",
    zona: "Interior",
    interior: false,
    exterior: false,
    zona_interior: "",
    zona_exterior: "",
    nivel_infestare: "",
    infestare_extra: "",
    nr_reprize: "0",
    interval_ore: "0",
    durata_minute: "0",
    temperatura: "0",
    temperatura_apa: "0",
    obiectiv_sub: "",
    obiectiv_detalii: [],
    obiectiv_detaliu_extra: "",
    obiectiv_altele: "",
    daunatori_altele: "",
    tehnician: "",
    lot: "",
    persoana_contact: "",
    telefon_contact: "",
    email_contact: "",
    coduri_caen: [],
    contract_data: "",
    durata_luni_contract: "",
    frecventa_luni_contract: "",
  });
  const [zones, setZones] = useState({
    interior: { servicii: [], servicii_detalii: {}, modalitati: [], metode_aplicare: {}, tipDaunatori: [], daunatori_by_service: {}, nivel_by_service: {}, substante: [], nivel_infestare: "", infestare_extra: "", suprafata_mp: "", nr_reprize: "", interval_ore: "", durata_minute: "", ora_start: "", ora_final: "", temperatura: "", tehnician: "" },
    exterior: { servicii: [], servicii_detalii: {}, modalitati: [], metode_aplicare: {}, tipDaunatori: [], daunatori_by_service: {}, nivel_by_service: {}, substante: [], nivel_infestare: "", infestare_extra: "", suprafata_mp: "", nr_reprize: "", interval_ore: "", durata_minute: "", ora_start: "", ora_final: "", temperatura: "", tehnician: "" },
  });
  const [stocksList, setStocksList] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (isEdit && editing) {
        // Pre-fill from existing PV
        setForm({
          numar: editing.numar || "",
          data: editing.data || new Date().toISOString().slice(0, 10),
          tip: editing.tip || "Servicii DDD",
          descriere: editing.descriere || "",
          observatii: editing.observatii || "",
          locatie: editing.locatie || "",
          ora_start: editing.ora_start || "",
          ora_final: editing.ora_final || "",
          suprafata_mp: editing.suprafata_mp != null ? String(editing.suprafata_mp) : "",
          metoda: editing.metoda || "",
          tehnician: editing.tehnician || user?.name || "",
          contract_numar: editing.contract_numar || "",
          recomandari: editing.recomandari || "",
          tip_obiectiv: editing.tip_obiectiv || "",
          obiectiv_sub: "",
          obiectiv_detalii: [],
          obiectiv_detaliu_extra: "",
          obiectiv_altele: "",
          zona: editing.zona || "Interior",
          interior: editing.interior ?? (editing.zona || "").toLowerCase().includes("interior"),
          exterior: editing.exterior ?? (editing.zona || "").toLowerCase().includes("exterior"),
          zona_interior: editing.zona_interior || "",
          zona_exterior: editing.zona_exterior || "",
          nivel_infestare: editing.nivel_infestare || "",
          interval_ore: editing.interval_ore != null ? String(editing.interval_ore) : "",
          durata_minute: editing.durata_minute != null ? String(editing.durata_minute) : "",
          temperatura: editing.temperatura != null ? String(editing.temperatura) : "",
          temperatura_apa: editing.temperatura_apa != null ? String(editing.temperatura_apa) : "",
          daunatori_altele: "",
          lot: editing.lot || "",
          persoana_contact: editing.persoana_contact || "",
          telefon_contact: editing.telefon_contact || "",
          email_contact: editing.email_contact || "",
          coduri_caen: Array.isArray(editing.coduri_caen) ? editing.coduri_caen : [],
          contract_data: editing.contract_data || "",
          durata_luni_contract: editing.durata_luni_contract != null ? String(editing.durata_luni_contract) : "",
          frecventa_luni_contract: editing.frecventa_luni_contract != null ? String(editing.frecventa_luni_contract) : "",
        });
        const iSubs = (editing.substante || []).filter((s) => (s.zona || "").toLowerCase() === "interior");
        const eSubs = (editing.substante || []).filter((s) => (s.zona || "").toLowerCase() === "exterior");
        const unzoned = (editing.substante || []).filter((s) => !s.zona);
        const isExt = !!editing.exterior && !editing.interior;
        setZones({
          interior: {
            servicii: editing.interior ? normalizeServices(editing.servicii || []) : [],
            servicii_detalii: (editing.interior_data?.servicii_detalii) || (editing.interior ? (editing.servicii_detalii || {}) : {}),
            tipDaunatori: editing.interior ? (editing.tip_daunatori || []) : [],
            daunatori_by_service: editing.interior ? normalizeSvcKeyedMap(editing.interior_data?.daunatori_by_service || editing.daunatori_by_service || {}) : {},
            nivel_by_service: editing.interior ? normalizeSvcKeyedMap(editing.interior_data?.nivel_by_service || editing.nivel_by_service || {}) : {},
            modalitati: editing.interior ? (editing.interior_data?.modalitati || editing.modalitate_interventie || []) : [],
            metode_aplicare: editing.interior ? normalizeSvcKeyedMap(editing.interior_data?.metode_aplicare || editing.metode_aplicare || {}) : {},
            substante: iSubs.length ? iSubs : (editing.interior && !isExt ? unzoned : []),
            nivel_infestare: editing.interior ? (editing.nivel_infestare || "") : "",
            infestare_extra: editing.interior ? (editing.infestare_extra || "") : "",
            suprafata_mp: editing.interior ? (editing.suprafata_mp != null ? String(editing.suprafata_mp) : "") : "",
            nr_reprize: editing.interior ? (editing.nr_reprize != null ? String(editing.nr_reprize) : "") : "",
            interval_ore: editing.interior ? (editing.interval_ore != null ? String(editing.interval_ore) : "") : "",
            durata_minute: editing.interior ? (editing.durata_minute != null ? String(editing.durata_minute) : "") : "",
            ora_start: editing.interior ? (editing.ora_start || "") : "",
            ora_final: editing.interior ? (editing.ora_final || "") : "",
            temperatura: editing.interior ? (editing.temperatura != null ? String(editing.temperatura) : "") : "",
            tehnician: editing.interior ? (editing.tehnician || user?.name || "") : (user?.name || ""),
          },
          exterior: {
            servicii: editing.exterior ? normalizeServices(editing.servicii || []) : [],
            servicii_detalii: (editing.exterior_data?.servicii_detalii) || (editing.exterior ? (editing.servicii_detalii || {}) : {}),
            tipDaunatori: editing.exterior ? (editing.tip_daunatori || []) : [],
            daunatori_by_service: editing.exterior ? normalizeSvcKeyedMap(editing.exterior_data?.daunatori_by_service || editing.daunatori_by_service || {}) : {},
            nivel_by_service: editing.exterior ? normalizeSvcKeyedMap(editing.exterior_data?.nivel_by_service || editing.nivel_by_service || {}) : {},
            modalitati: editing.exterior ? (editing.exterior_data?.modalitati || editing.modalitate_interventie || []) : [],
            metode_aplicare: editing.exterior ? normalizeSvcKeyedMap(editing.exterior_data?.metode_aplicare || editing.metode_aplicare || {}) : {},
            substante: eSubs.length ? eSubs : (isExt ? unzoned : []),
            nivel_infestare: editing.exterior ? (editing.nivel_infestare || "") : "",
            infestare_extra: editing.exterior ? (editing.infestare_extra || "") : "",
            suprafata_mp: editing.exterior ? (editing.suprafata_mp != null ? String(editing.suprafata_mp) : "") : "",
            nr_reprize: editing.exterior ? (editing.nr_reprize != null ? String(editing.nr_reprize) : "") : "",
            interval_ore: editing.exterior ? (editing.interval_ore != null ? String(editing.interval_ore) : "") : "",
            durata_minute: editing.exterior ? (editing.durata_minute != null ? String(editing.durata_minute) : "") : "",
            ora_start: editing.exterior ? (editing.ora_start || "") : "",
            ora_final: editing.exterior ? (editing.ora_final || "") : "",
            temperatura: editing.exterior ? (editing.temperatura != null ? String(editing.temperatura) : "") : "",
            tehnician: editing.exterior ? (editing.tehnician || user?.name || "") : (user?.name || ""),
          },
        });
        setTemplate(editing.template || "generic");
        setAutoNumber(false);
        setLinkedContract(editing.contract_id ? {
          id: editing.contract_id,
          numar: editing.contract_numar,
          data: editing.contract_data,
        } : null);
        return;
      }
      setForm({
        numar: "", data: new Date().toISOString().slice(0, 10),
        tip: "Servicii DDD", descriere: "", observatii: "",
        locatie: "", ora_start: "", ora_final: "",
        suprafata_mp: "0", metoda: "", tehnician: "", contract_numar: "",
        recomandari: "",
        tip_obiectiv: "",
        obiectiv_sub: "",
        obiectiv_detalii: [],
        obiectiv_detaliu_extra: "",
        obiectiv_altele: "",
        zona: "Interior",
        interior: false,
        exterior: false,
        zona_interior: "",
        zona_exterior: "",
        nivel_infestare: "",
    infestare_extra: "",
        nr_reprize: "0",
        interval_ore: "0",
        durata_minute: "0",
        temperatura: "0",
        temperatura_apa: "0",
        daunatori_altele: "",
        tehnician: user?.name || "",
        lot: "",
        persoana_contact: "",
        telefon_contact: "",
        email_contact: "",
        coduri_caen: [],
        contract_data: "",
        durata_luni_contract: "",
        frecventa_luni_contract: "",
      });
      setZones({
        interior: { servicii: [], servicii_detalii: {}, modalitati: [], metode_aplicare: {}, tipDaunatori: [], daunatori_by_service: {}, nivel_by_service: {}, substante: [], nivel_infestare: "", infestare_extra: "", suprafata_mp: "", nr_reprize: "", interval_ore: "", durata_minute: "", ora_start: nowHHMM(), ora_final: "", temperatura: "", tehnician: user?.name || "" },
        exterior: { servicii: [], servicii_detalii: {}, modalitati: [], metode_aplicare: {}, tipDaunatori: [], daunatori_by_service: {}, nivel_by_service: {}, substante: [], nivel_infestare: "", infestare_extra: "", suprafata_mp: "", nr_reprize: "", interval_ore: "", durata_minute: "", ora_start: nowHHMM(), ora_final: "", temperatura: "", tehnician: user?.name || "" },
      });
      setTemplate("ddd");
      setAutoNumber(true);
      setLinkedContract(null);
      // Fetch latest contract + all stocks (for substance picker)
      (async () => {
        try {
          const { data: stocks } = await api.get("/stocks");
          setStocksList(stocks || []);
        } catch { setStocksList([]); }
        // If a specific contract was pre-selected (e.g. from a contract row's "+ PV" button), use it.
        // Otherwise fall back to the latest contract for this client.
        try {
          let ct;
          if (preselectedContract && preselectedContract.id) {
            try {
              const { data: full } = await api.get(`/contracts/${preselectedContract.id}`);
              ct = full;
            } catch {
              ct = preselectedContract;
            }
          } else {
            const { data: latest } = await api.get(`/clients/${clientId}/contracts/latest`);
            ct = latest;
          }
          setLinkedContract(ct);
          // Auto-fill PV from linked contract — locație, servicii, suprafață, interior/exterior flags
          const svc = normalizeServices(ct.servicii_prestari);
          const iSup = Number(ct.suprafata_mp_interior || 0);
          const eSup = Number(ct.suprafata_mp_exterior || 0);
          const hasInterior = iSup > 0;
          const hasExterior = eSup > 0;
          setForm((f) => ({
            ...f,
            contract_numar: ct.numar || "",
            contract_data: f.contract_data || ct.data || "",
            durata_luni_contract: f.durata_luni_contract || (ct.durata_luni != null ? String(ct.durata_luni) : ""),
            frecventa_luni_contract: f.frecventa_luni_contract || (ct.frecventa_luni != null ? String(ct.frecventa_luni) : ""),
            locatie: f.locatie || ct.locatie || ct.obiect || "",
            interior: f.interior || hasInterior || (!hasInterior && !hasExterior),
            exterior: f.exterior || hasExterior,
            // Auto-fill "Din ce este alcătuit punctul de lucru" from contract
            obiectiv_detalii: (f.obiectiv_detalii && f.obiectiv_detalii.length) ? f.obiectiv_detalii : (ct.obiectiv_detalii || []),
            obiectiv_detaliu_extra: f.obiectiv_detaliu_extra || ct.obiectiv_detaliu_extra || "",
            tip_obiectiv: f.tip_obiectiv || ct.tip_obiectiv || "",
            obiectiv_sub: f.obiectiv_sub || ct.obiectiv_sub || "",
            // Auto-fill contact info from contract (persoană, telefon, email)
            persoana_contact: f.persoana_contact || ct.persoana_contact || "",
            telefon_contact: f.telefon_contact || ct.telefon_contact || "",
            email_contact: f.email_contact || ct.email_contact || "",
            // CAEN codes from contract → PV
            coduri_caen: (f.coduri_caen && f.coduri_caen.length) ? f.coduri_caen : (Array.isArray(ct.coduri_caen) ? ct.coduri_caen : []),
            // Contract observations → PV observations (only if PV's own is still empty)
            observatii: f.observatii || ct.observatii || "",
          }));
          // Propagate servicii + suprafață in every zone we activated
          setZones((z) => ({
            interior: {
              ...z.interior,
              servicii: z.interior.servicii.length ? z.interior.servicii : (hasInterior || (!hasInterior && !hasExterior) ? svc : []),
              suprafata_mp: z.interior.suprafata_mp || (iSup ? String(iSup) : ""),
            },
            exterior: {
              ...z.exterior,
              servicii: z.exterior.servicii.length ? z.exterior.servicii : (hasExterior ? svc : []),
              suprafata_mp: z.exterior.suprafata_mp || (eSup ? String(eSup) : ""),
            },
          }));
          try {
            const { data: nn } = await api.get("/pvs/next-number", {
              params: { template: "ddd", contract_id: ct.id },
            });
            setForm((f) => ({ ...f, numar: nn.numar || "" }));
          } catch {}
        } catch {
          setLinkedContract(null);
          try {
            const { data: nn } = await api.get("/pvs/next-number", { params: { template: "ddd" } });
            setForm((f) => ({ ...f, numar: nn.numar || "" }));
          } catch {}
        }
      })();
    }
  }, [open, clientId, isEdit, editing, preselectedContract]);

  // ── Auto-save draft to localStorage (only for NEW PVs, not edits) ──
  const draftKey = clientId ? `pv_draft_${clientId}` : null;
  const [draftRestored, setDraftRestored] = useState(false);

  // On open (new PV), restore draft if any
  useEffect(() => {
    if (!open || isEdit || !draftKey) return;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const d = JSON.parse(raw);
      if (d && (d.form || d.zones)) {
        // Delay slightly so initial reset doesn't overwrite
        setTimeout(() => {
          if (d.form) setForm((f) => ({ ...f, ...d.form }));
          if (d.zones) {
            // Normalize servicii keys/values so old drafts (mixed diacritics)
            // don't render duplicate service panels.
            const norm = { ...d.zones };
            ["interior", "exterior"].forEach((k) => {
              if (norm[k]) {
                norm[k] = {
                  ...norm[k],
                  servicii: normalizeServices(norm[k].servicii || []),
                  daunatori_by_service: normalizeSvcKeyedMap(norm[k].daunatori_by_service || {}),
                  nivel_by_service: normalizeSvcKeyedMap(norm[k].nivel_by_service || {}),
                  metode_aplicare: normalizeSvcKeyedMap(norm[k].metode_aplicare || {}),
                };
              }
            });
            setZones((z) => ({ ...z, ...norm }));
          }
          setDraftRestored(true);
          toast.info("Ai un draft nesalvat — l-am restaurat.", { duration: 4000 });
        }, 300);
      }
    } catch {}
    // eslint-disable-next-line
  }, [open, isEdit, draftKey]);

  // Persist draft on any change (debounced)
  useEffect(() => {
    if (!open || isEdit || !draftKey) return;
    const t = setTimeout(() => {
      try { localStorage.setItem(draftKey, JSON.stringify({ form, zones, ts: Date.now() })); } catch {}
    }, 500);
    return () => clearTimeout(t);
  }, [form, zones, open, isEdit, draftKey]);

  const clearDraft = () => {
    if (draftKey) { try { localStorage.removeItem(draftKey); } catch {} }
    setDraftRestored(false);
  };

  const updateZone = (zone, patch) =>
    setZones((cur) => ({ ...cur, [zone]: { ...cur[zone], ...patch } }));

  const toggleService = (zone, s) =>
    setZones((cur) => {
      const norm = normalizeService(s);
      const existing = cur[zone].servicii.map(normalizeService);
      const already = existing.includes(norm);
      const next = already
        ? existing.filter((x) => x !== norm)
        : Array.from(new Set([...existing, norm]));
      return {
        ...cur,
        [zone]: {
          ...cur[zone],
          servicii: next,
        },
      };
    });

  const toggleDaunator = (zone, d) =>
    setZones((cur) => ({
      ...cur,
      [zone]: {
        ...cur[zone],
        tipDaunatori: cur[zone].tipDaunatori.includes(d)
          ? cur[zone].tipDaunatori.filter((x) => x !== d)
          : [...cur[zone].tipDaunatori, d],
      },
    }));

  const MAX_SUBS = 5;
  const countSvc = (zone, svc, exceptIdx = -1) =>
    zones[zone].substante.filter((s, idx) => idx !== exceptIdx && (s.serviciu || "DEZINSECȚIE") === svc).length;

  const addSubstanta = (zone) => {
    const svc = ["DEZINSECȚIE", "DERATIZARE", "DEZINFECȚIE", ...zones[zone].servicii]
      .filter((x, i, arr) => arr.indexOf(x) === i)
      .sort((a, b) => zones[zone].servicii.includes(b) - zones[zone].servicii.includes(a))
      .find((x) => countSvc(zone, x) < MAX_SUBS);
    if (!svc) {
      toast.error(`Maxim ${MAX_SUBS} substanțe pentru fiecare serviciu.`);
      return;
    }
    setZones((cur) => ({
      ...cur,
      [zone]: {
        ...cur[zone],
        substante: [...cur[zone].substante, {
          denumire: "", substanta_activa: "", aviz: "", mod_aplicare: "", compozitie: "",
          serviciu: svc,
          consum_concentrata: "", consum_total: "", timp_actiune: "", lot: "",
        }],
      },
    }));
  };

  const changeSubstantaServiciu = (zone, i, svc) => {
    if (countSvc(zone, svc, i) >= MAX_SUBS) {
      toast.error(`${svc}: ai deja ${MAX_SUBS} substanțe (maxim permis).`);
      return;
    }
    updateSubstanta(zone, i, "serviciu", svc);
  };

  const pickFromStock = (zone, i, stockId) => {
    const stock = stocksList.find((s) => s.id === stockId);
    if (!stock) return;
    setZones((cur) => ({
      ...cur,
      [zone]: {
        ...cur[zone],
        substante: cur[zone].substante.map((s, idx) => (idx === i ? {
          ...s,
          denumire: stock.denumire || s.denumire,
          // Auto-fill toate câmpurile din stoc (substanța activă, aviz autorizație, consum, timp acțiune)
          substanta_activa: stock.substanta_activa || s.substanta_activa || "",
          compozitie: stock.substanta_activa || s.compozitie || "",
          aviz: stock.aviz_autorizatie || stock.aviz_lot || s.aviz || "",
          aviz_lot: stock.aviz_lot || s.aviz_lot || "",
          aviz_autorizatie: stock.aviz_autorizatie || s.aviz_autorizatie || "",
          consum_concentrata: stock.consum_concentrata || s.consum_concentrata || "",
          consum_total: stock.consum_total || s.consum_total || "",
          timp_actiune: stock.timp_actiune || s.timp_actiune || "",
          lot: stock.lot_curent || stock.lot || s.lot || "",
          stock_id: stock.id,
        } : s)),
      },
    }));
  };

  const updateSubstanta = (zone, i, key, val) =>
    setZones((cur) => ({
      ...cur,
      [zone]: {
        ...cur[zone],
        substante: cur[zone].substante.map((s, idx) => (idx === i ? { ...s, [key]: val } : s)),
      },
    }));

  const removeSubstanta = (zone, i) =>
    setZones((cur) => ({
      ...cur,
      [zone]: {
        ...cur[zone],
        substante: cur[zone].substante.filter((_, idx) => idx !== i),
      },
    }));

  const buildPayload = () => {
    const payload = {
      numar: form.numar,
      data: form.data,
      tip: template === "ddd" ? "Servicii DDD" : form.tip,
      descriere: form.descriere,
      observatii: form.observatii,
      template,
      persoana_contact: form.persoana_contact || "",
      telefon_contact: form.telefon_contact || "",
      email_contact: form.email_contact || "",
      coduri_caen: form.coduri_caen || [],
      contract_data: form.contract_data || "",
      durata_luni_contract: form.durata_luni_contract ? Number(form.durata_luni_contract) : null,
      frecventa_luni_contract: form.frecventa_luni_contract ? Number(form.frecventa_luni_contract) : null,
    };
    if (template === "ddd") {
      Object.assign(payload, {
        locatie: form.locatie,
        ora_start: zones.interior.ora_start || zones.exterior.ora_start || form.ora_start,
        ora_final: zones.interior.ora_final || zones.exterior.ora_final || form.ora_final,
        servicii: [...new Set([...zones.interior.servicii, ...zones.exterior.servicii])],
        servicii_detalii: { ...zones.interior.servicii_detalii, ...zones.exterior.servicii_detalii },
        interior_data: { servicii: zones.interior.servicii, servicii_detalii: zones.interior.servicii_detalii, modalitati: zones.interior.modalitati, metode_aplicare: zones.interior.metode_aplicare, daunatori_by_service: zones.interior.daunatori_by_service, nivel_by_service: zones.interior.nivel_by_service, tip_daunatori: zones.interior.tipDaunatori, substante: zones.interior.substante, nivel_infestare: zones.interior.nivel_infestare },
        exterior_data: { servicii: zones.exterior.servicii, servicii_detalii: zones.exterior.servicii_detalii, modalitati: zones.exterior.modalitati, metode_aplicare: zones.exterior.metode_aplicare, daunatori_by_service: zones.exterior.daunatori_by_service, nivel_by_service: zones.exterior.nivel_by_service, tip_daunatori: zones.exterior.tipDaunatori, substante: zones.exterior.substante, nivel_infestare: zones.exterior.nivel_infestare },
        modalitate_interventie: [...new Set([...(zones.interior.modalitati || []), ...(zones.exterior.modalitati || [])])],
        daunatori_by_service: (() => {
          const merged = {};
          ["DEZINSECȚIE", "DERATIZARE", "DEZINFECȚIE"].forEach((svc) => {
            const set = new Set([
              ...((zones.interior.daunatori_by_service || {})[svc] || []),
              ...((zones.exterior.daunatori_by_service || {})[svc] || []),
            ]);
            if (set.size) merged[svc] = [...set];
          });
          return merged;
        })(),
        nivel_by_service: (() => {
          const merged = {};
          ["DEZINSECȚIE", "DERATIZARE", "DEZINFECȚIE"].forEach((svc) => {
            const val = (zones.interior.nivel_by_service || {})[svc] || (zones.exterior.nivel_by_service || {})[svc] || "";
            if (val) merged[svc] = val;
          });
          return merged;
        })(),
        metode_aplicare: (() => {
          const merged = {};
          ["DEZINSECȚIE", "DERATIZARE", "DEZINFECȚIE"].forEach((svc) => {
            const set = new Set([
              ...((zones.interior.metode_aplicare || {})[svc] || []),
              ...((zones.exterior.metode_aplicare || {})[svc] || []),
            ]);
            if (set.size) merged[svc] = [...set];
          });
          return merged;
        })(),
        suprafata_mp: (() => {
          const i = parseFloat(zones.interior.suprafata_mp || 0);
          const e = parseFloat(zones.exterior.suprafata_mp || 0);
          const total = i + e;
          return total > 0 ? total : (form.suprafata_mp ? parseFloat(form.suprafata_mp) : null);
        })(),
        substante: [
          ...zones.interior.substante.map((s) => ({ ...s, zona: "Interior" })),
          ...zones.exterior.substante.map((s) => ({ ...s, zona: "Exterior" })),
        ],
        metoda: form.metoda,
        recomandari: form.recomandari,
        tehnician: form.tehnician,
        contract_numar: form.contract_numar,
        contract_id: linkedContract?.id || "",
        contract_data: linkedContract?.data || "",
        lot: isAdmin ? (form.lot || "") : "",
        tip_obiectiv: (() => {
          if (form.tip_obiectiv === "Altele") return `Altele: ${form.obiectiv_altele || "—"}`;
          const parts = [form.tip_obiectiv, form.obiectiv_sub];
          if (form.obiectiv_detalii?.length) parts.push(form.obiectiv_detalii.join(" + "));
          if (form.obiectiv_detaliu_extra?.trim()) parts.push(form.obiectiv_detaliu_extra.trim());
          return parts.filter(Boolean).join(" • ");
        })(),
        zona: (() => {
          if (form.interior && form.exterior) return "Interior + Exterior";
          if (form.interior) return "Interior";
          if (form.exterior) return "Exterior";
          return "";
        })(),
        interior: !!form.interior,
        exterior: !!form.exterior,
        zona_interior: form.interior ? (form.zona_interior || "") : "",
        zona_exterior: form.exterior ? (form.zona_exterior || "") : "",
        tip_daunatori: [
          ...new Set([
            ...zones.interior.tipDaunatori,
            ...zones.exterior.tipDaunatori,
            ...Object.values(zones.interior.daunatori_by_service || {}).flat(),
            ...Object.values(zones.exterior.daunatori_by_service || {}).flat(),
          ]),
          ...(form.daunatori_altele ? [form.daunatori_altele] : []),
        ],
        nivel_infestare: zones.interior.nivel_infestare || zones.exterior.nivel_infestare || Object.values(zones.interior.nivel_by_service || {}).find(Boolean) || Object.values(zones.exterior.nivel_by_service || {}).find(Boolean) || "",
        infestare_extra: zones.interior.infestare_extra || zones.exterior.infestare_extra || "",
        interval_ore: (zones.interior.interval_ore || zones.exterior.interval_ore || form.interval_ore) ? parseFloat(zones.interior.interval_ore || zones.exterior.interval_ore || form.interval_ore) : null,
        temperatura: (zones.interior.temperatura || zones.exterior.temperatura || form.temperatura) ? parseFloat(zones.interior.temperatura || zones.exterior.temperatura || form.temperatura) : null,
        temperatura_apa: form.temperatura_apa ? parseFloat(form.temperatura_apa) : null,
        durata_minute: (zones.interior.durata_minute || zones.exterior.durata_minute || form.durata_minute) ? parseInt(zones.interior.durata_minute || zones.exterior.durata_minute || form.durata_minute) : null,
        nr_reprize: (zones.interior.nr_reprize || zones.exterior.nr_reprize || form.nr_reprize) ? parseInt(zones.interior.nr_reprize || zones.exterior.nr_reprize || form.nr_reprize) : null,
        tehnician: zones.interior.tehnician || zones.exterior.tehnician || form.tehnician,
      });
    }
    return payload;
  };

  const [previewing, setPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null); // Blob URL of last preview
  const [showPreviewPanel, setShowPreviewPanel] = useState(false);

  const generatePreview = async () => {
    if (!form.numar || !form.data) {
      toast.error("Număr și dată sunt obligatorii");
      return;
    }
    setPreviewing(true);
    try {
      const payload = buildPayload();
      payload.client_id = clientId;
      const { data } = await api.post("/pvs/preview-pdf", payload, { responseType: "blob" });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      const url = URL.createObjectURL(data);
      setPreviewUrl(url);
      setShowPreviewPanel(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare previzualizare");
    } finally {
      setPreviewing(false);
    }
  };

  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl); };
    // eslint-disable-next-line
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.numar || !form.data) {
      toast.error("Număr și dată sunt obligatorii");
      return;
    }
    if (template === "generic" && !form.descriere) {
      toast.error("Descrierea este obligatorie pentru PV generic");
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isEdit) {
        await api.patch(`/pvs/${editing.id}`, payload);
        toast.success("PV actualizat");
      } else {
        const { data: created } = await api.post(`/clients/${clientId}/pvs`, payload);
        if (created?.numar && created.numar !== payload.numar) {
          toast.success(`PV adăugat cu nr. ${created.numar} (${payload.numar} fusese deja folosit de alt utilizator)`);
        } else {
          toast.success("PV adăugat");
        }
      }
      clearDraft();
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="w-[95vw] max-w-4xl max-h-[85dvh] xl:max-h-[92dvh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Proces Verbal de Execuție</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          {/* Fixed to DDD template; generic PV removed per user request */}

          {/* Common */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="flex items-center justify-between">
                <Label className="label-eyebrow">Număr *</Label>
                <button
                  type="button"
                  onClick={() => setAutoNumber((v) => !v)}
                  className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border transition-colors ${
                    autoNumber
                      ? "border-gold bg-gold-soft text-gold-deep"
                      : "border-border text-muted-foreground hover:border-slate-400"
                  }`}
                  data-testid="pv-auto-number-toggle"
                  title="Comută între numerotare automată și manuală"
                >
                  {autoNumber ? "AUTO" : "MANUAL"}
                </button>
              </div>
              <Input value={form.numar} onChange={(e) => { setAutoNumber(false); setForm({ ...form, numar: e.target.value }); }} className="mt-2 h-11 font-mono-crm" required data-testid="pv-numar-input" />
            </div>
            <div>
              <Label className="label-eyebrow">Data *</Label>
              <Input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} className="mt-2 h-11" required data-testid="pv-data-input" />
            </div>
          </div>

          {template === "generic" ? (
            <>
              <div>
                <Label className="label-eyebrow">Tip</Label>
                <Select value={form.tip} onValueChange={(v) => setForm({ ...form, tip: v })}>
                  <SelectTrigger className="mt-2 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Predare-Primire">Predare-Primire</SelectItem>
                    <SelectItem value="Recepție">Recepție</SelectItem>
                    <SelectItem value="Constatare">Constatare</SelectItem>
                    <SelectItem value="Custodie">Custodie</SelectItem>
                    <SelectItem value="Servicii prestate">Servicii prestate</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-eyebrow">Descriere *</Label>
                <Textarea value={form.descriere} onChange={(e) => setForm({ ...form, descriere: e.target.value })} className="mt-2" rows={4} required data-testid="pv-descriere-input" />
              </div>
            </>
          ) : (
            <>
              {/* Contract linked (auto-filled from latest) + locație */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-eyebrow">Contract asociat (auto)</Label>
                  <Input
                    value={linkedContract ? `${linkedContract.numar}${linkedContract.data ? ` · ${new Date(linkedContract.data).toLocaleDateString("ro-RO")}` : ""}` : "—"}
                    readOnly
                    disabled
                    className="mt-2 h-11 bg-slate-50 cursor-not-allowed"
                    data-testid="pv-contract-linked"
                  />
                </div>
                <div>
                  <Label className="label-eyebrow">Locația intervenției</Label>
                  <Input value={form.locatie} onChange={(e) => setForm({ ...form, locatie: e.target.value })} className="mt-2 h-11" placeholder="Adresă (dacă diferă de sediu)" data-testid="pv-locatie-input" />
                </div>
              </div>

              {/* Tipul obiectivului — dropdown cu scroll */}
              <div>
                <Label className="label-eyebrow">Tipul obiectivului</Label>
                <select
                  value={form.tip_obiectiv || ""}
                  onChange={(e) => setForm({ ...form, tip_obiectiv: e.target.value, obiectiv_sub: "", obiectiv_detalii: [], obiectiv_detaliu_extra: "" })}
                  className="mt-2 h-11 w-full border border-input rounded-md bg-white px-3 text-sm"
                  data-testid="pv-obiectiv-select"
                >
                  <option value="">— Alege tipul —</option>
                  {TIPURI_OBIECTIV.map((t) => (
                    <option key={t.label} value={t.label}>{t.label}</option>
                  ))}
                </select>

                {/* „Altele" — text liber */}
                {form.tip_obiectiv === "Altele" && (
                  <div className="mt-3">
                    <Label className="label-eyebrow">Specifică „Altele"</Label>
                    <Input
                      value={form.obiectiv_altele || ""}
                      onChange={(e) => setForm({ ...form, obiectiv_altele: e.target.value })}
                      className="mt-2 h-11"
                      placeholder="Descrie obiectivul"
                      data-testid="pv-obiectiv-altele"
                    />
                  </div>
                )}

                {/* Din ce este alcătuit punctul de lucru — bife multiple */}
                {(() => {
                  const t = TIPURI_OBIECTIV.find((x) => x.label === form.tip_obiectiv);
                  if (!t || !t.details || t.details.length === 0) return null;
                  return (
                    <div className="mt-4">
                      <Label className="label-eyebrow">Din ce este alcătuit punctul de lucru</Label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {t.details.map((d) => {
                          const on = (form.obiectiv_detalii || []).includes(d);
                          return (
                            <button
                              type="button"
                              key={d}
                              onClick={() => {
                                const cur = form.obiectiv_detalii || [];
                                setForm({
                                  ...form,
                                  obiectiv_detalii: on ? cur.filter((x) => x !== d) : [...cur, d],
                                });
                              }}
                              data-testid={`pv-obiectiv-detail-${d}`}
                              className={`text-xs border rounded-md px-3 py-1.5 transition-colors ${
                                on
                                  ? "border-gold bg-gold text-white"
                                  : "border-border bg-white hover:border-gold"
                              }`}
                            >
                              {on ? "☑ " : "☐ "}{d}
                            </button>
                          );
                        })}
                      </div>
                      <div className="mt-3">
                        <Label className="label-eyebrow">Adaugă subcategorie (text liber)</Label>
                        <Input
                          value={form.obiectiv_detaliu_extra || ""}
                          onChange={(e) => setForm({ ...form, obiectiv_detaliu_extra: e.target.value })}
                          className="mt-2 h-11"
                          placeholder="ex: Etaj 2 aripa dreapta, hol lângă intrare, cameră centrală..."
                          data-testid="pv-obiectiv-detaliu-extra"
                        />
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Zonă aplicație */}
              <div>
                <Label className="label-eyebrow">Zonă aplicație</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, interior: !form.interior })}
                    data-testid="pv-zona-interior"
                    className={`px-4 py-2 text-sm font-semibold rounded-md border transition-colors ${
                      form.interior
                        ? "border-blue-500 bg-blue-100 text-blue-900 ring-2 ring-blue-400/40"
                        : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    📍 Interior
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, exterior: !form.exterior })}
                    data-testid="pv-zona-exterior"
                    className={`px-4 py-2 text-sm font-semibold rounded-md border transition-colors ${
                      form.exterior
                        ? "border-emerald-500 bg-emerald-100 text-emerald-900 ring-2 ring-emerald-400/40"
                        : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    📍 Exterior
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">
                  Bifează una sau ambele. Pentru fiecare zonă bifată se deschide un panou complet cu Servicii, Dăunători, Nivel și Substanțe.
                </p>
              </div>

              {/* Tip dăunători + Nivel intensitate — MUTATE ÎN BOXELE ZONA */}

              {/* Suprafață + detalii execuție — MUTATE ÎN BOXELE ZONA */}

              {/* Boxe pe zonă: Interior + Exterior — fiecare cu Servicii + Dăunători + Nivel + Substanțe */}
              {[
                { key: "interior", title: "INTERIOR", visible: form.interior, boxClass: "border-blue-400 bg-blue-50/40", titleClass: "text-blue-900" },
                { key: "exterior", title: "EXTERIOR", visible: form.exterior, boxClass: "border-emerald-400 bg-emerald-50/40", titleClass: "text-emerald-900" },
              ].map(({ key, title, visible, boxClass, titleClass }) => !visible ? null : (
                <div key={key} className={`border-2 rounded-lg p-4 space-y-4 ${boxClass}`} data-testid={`pv-zone-box-${key}`}>
                  <h3 className={`font-display font-black text-xl ${titleClass} flex items-center gap-2`}>
                    <span>📍 {title}</span>
                  </h3>
                  {/* Servicii */}
                  <div>
                    <Label className="label-eyebrow">Servicii prestate — {title}</Label>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      {["DEZINSECȚIE", "DEZINFECȚIE", "DERATIZARE"].map((s) => {
                        const active = zones[key].servicii.includes(s);
                        return (
                          <button
                            type="button"
                            key={s}
                            onClick={() => toggleService(key, s)}
                            data-testid={`pv-${key}-service-${s}`}
                            className={`text-xs font-semibold border rounded-md py-3 transition-colors ${active ? "border-gold bg-gold-soft text-gold-deep" : "border-border bg-white hover:border-slate-400"}`}
                          >
                            {active ? "☑ " : "☐ "} {s}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {/* Tip dăunători + Nivel intensitate — sub-coloane per serviciu bifat */}
                  {zones[key].servicii.length > 0 ? (
                    <div className={`grid gap-3 ${zones[key].servicii.length === 1 ? "grid-cols-1" : zones[key].servicii.length === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-3"}`}>
                      {zones[key].servicii.map((svc) => {
                        const dOptions = DAUNATORI_BY_SERVICE[svc] || [];
                        const dSelected = (zones[key].daunatori_by_service || {})[svc] || [];
                        const nivelSelected = (zones[key].nivel_by_service || {})[svc] || "";
                        const toggleD = (d) => setZones((cur) => {
                          const map = { ...(cur[key].daunatori_by_service || {}) };
                          const list = map[svc] || [];
                          map[svc] = list.includes(d) ? list.filter((x) => x !== d) : [...list, d];
                          return { ...cur, [key]: { ...cur[key], daunatori_by_service: map } };
                        });
                        const setNivel = (v) => setZones((cur) => {
                          const map = { ...(cur[key].nivel_by_service || {}) };
                          map[svc] = map[svc] === v ? "" : v;
                          return { ...cur, [key]: { ...cur[key], nivel_by_service: map } };
                        });
                        return (
                          <div key={svc} className="border-2 border-slate-200 rounded-lg p-3 bg-white/80" data-testid={`pv-${key}-svc-panel-${svc}`}>
                            <div className="text-xs font-bold uppercase tracking-widest text-gold-deep mb-2">
                              {svc}
                            </div>
                            <Label className="label-eyebrow">Dăunători / agenți</Label>
                            <div className="mt-2">
                              <CheckMultiDropdown
                                placeholder="— Alege dăunătorii —"
                                options={dOptions}
                                selected={dSelected}
                                onToggle={toggleD}
                                testId={`pv-${key}-daunatori-details-${svc}`}
                                itemTestIdPrefix={`pv-${key}-daunator-${svc}`}
                              />
                            </div>
                            <div className="mt-3">
                              <Label className="label-eyebrow">Nivel infestare</Label>
                              <div className="mt-1.5 flex gap-1.5">
                                {["Prevenție", "Mică", "Medie", "Majoră"].map((v) => (
                                  <button
                                    type="button"
                                    key={v}
                                    onClick={() => setNivel(v)}
                                    data-testid={`pv-${key}-nivel-${svc}-${v}`}
                                    className={`text-[11px] border rounded-md py-1 px-2.5 transition-colors ${
                                      nivelSelected === v
                                        ? (v === "Majoră" ? "border-red-500 bg-red-50 text-red-700" : v === "Medie" ? "border-orange-500 bg-orange-50 text-orange-700" : v === "Prevenție" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-gold bg-gold-soft text-gold-deep")
                                        : "border-border bg-white hover:border-slate-400"
                                    }`}
                                  >
                                    {nivelSelected === v ? "☑ " : "☐ "}{v}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">Bifează cel puțin un serviciu ca să vezi coloanele cu dăunători și nivel.</p>
                  )}
                  {/* Extra detalii nivel (opțional) */}
                  <div>
                    <Label className="label-eyebrow">Detalii extra intensitate (opțional)</Label>
                    <Input
                      value={zones[key].infestare_extra || ""}
                      onChange={(e) => updateZone(key, { infestare_extra: e.target.value })}
                      className="mt-2 h-10"
                      placeholder="ex: focar în subsol, migrare din exterior..."
                    />
                  </div>
                  {/* Suprafață + Reprize + Interval + Durată + Ora start/final + Temperatură + Tehnician */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <Label className="label-eyebrow">Suprafață (mp²)</Label>
                      <Input type="number" step="0.1" value={zones[key].suprafata_mp} onChange={(e) => updateZone(key, { suprafata_mp: e.target.value })} className="mt-2 h-11 bg-white" placeholder="0" data-testid={`pv-${key}-suprafata`} />
                    </div>
                    <div>
                      <Label className="label-eyebrow">Reprize</Label>
                      <Input type="number" min="0" value={zones[key].nr_reprize} onChange={(e) => updateZone(key, { nr_reprize: e.target.value })} className="mt-2 h-11 bg-white" placeholder="0" />
                    </div>
                    <div>
                      <Label className="label-eyebrow">Interval (ore)</Label>
                      <Input type="number" step="0.5" value={zones[key].interval_ore} onChange={(e) => updateZone(key, { interval_ore: e.target.value })} className="mt-2 h-11 bg-white" placeholder="2" />
                    </div>
                    <div>
                      <Label className="label-eyebrow">Durată (min)</Label>
                      <Input type="number" min="0" value={zones[key].durata_minute} onChange={(e) => {
                        const durata = e.target.value;
                        const oraFinal = addMinutesToHHMM(zones[key].ora_start, durata);
                        updateZone(key, { durata_minute: durata, ora_final: oraFinal || zones[key].ora_final });
                      }} className="mt-2 h-11 bg-white" placeholder="60" data-testid={`pv-${key}-durata`} />
                    </div>
                    <div>
                      <Label className="label-eyebrow">Ora start</Label>
                      <Input type="time" value={zones[key].ora_start} onChange={(e) => {
                        const oraStart = e.target.value;
                        const oraFinal = addMinutesToHHMM(oraStart, zones[key].durata_minute);
                        updateZone(key, { ora_start: oraStart, ora_final: oraFinal || zones[key].ora_final });
                      }} className="mt-2 h-11 bg-white" data-testid={`pv-${key}-ora-start`} />
                    </div>
                    <div>
                      <Label className="label-eyebrow">Ora final <span className="text-[10px] text-muted-foreground normal-case">(auto)</span></Label>
                      <Input type="time" value={zones[key].ora_final} onChange={(e) => updateZone(key, { ora_final: e.target.value })} className="mt-2 h-11 bg-white" data-testid={`pv-${key}-ora-final`} />
                    </div>
                    <div>
                      <Label className="label-eyebrow">Temperatură (°C)</Label>
                      <Input type="number" step="0.1" value={zones[key].temperatura} onChange={(e) => updateZone(key, { temperatura: e.target.value })} className="mt-2 h-11 bg-white" placeholder="0" />
                    </div>
                    <div>
                      <Label className="label-eyebrow">Tehnician</Label>
                      <Input value={zones[key].tehnician} onChange={(e) => updateZone(key, { tehnician: e.target.value })} className="mt-2 h-11 bg-white" placeholder="Nume operator" />
                    </div>
                  </div>
                  {/* Substanțe */}
                  <div>
                    <div className="flex items-center justify-between">
                      <Label className="label-eyebrow">Materiale / substanțe utilizate — {title}</Label>
                      <Button type="button" size="sm" variant="outline" onClick={() => addSubstanta(key)} data-testid={`pv-${key}-add-substanta`}>
                        <Plus className="w-3.5 h-3.5 mr-1" /> Adaugă substanță
                      </Button>
                    </div>
                    {zones[key].substante.length === 0 ? (
                      <p className="text-xs text-muted-foreground mt-3 italic">Nicio substanță adăugată încă.</p>
                    ) : (
                      <div className="mt-3 space-y-3">
                        {zones[key].substante.map((s, i) => (
                          <div key={i} className="border border-border rounded-md p-3 bg-white space-y-2">
                            <div className="flex items-center justify-between">
                              <p className="label-eyebrow" data-testid={`sub-label-${key}-${i}`}>
                                Substanță #{i + 1} · {s.serviciu || "DEZINSECȚIE"} {zones[key].substante.slice(0, i + 1).filter((x) => (x.serviciu || "DEZINSECȚIE") === (s.serviciu || "DEZINSECȚIE")).length}/{MAX_SUBS}
                              </p>
                              <Button type="button" variant="ghost" size="sm" onClick={() => removeSubstanta(key, i)} className="text-red-600 hover:bg-red-50 h-7">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              <Select value={s.serviciu || "DEZINSECȚIE"} onValueChange={(v) => changeSubstantaServiciu(key, i, v)}>
                                <SelectTrigger className="h-9 text-sm" data-testid={`sub-serviciu-${key}-${i}`}><SelectValue placeholder="Serviciu" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="DEZINSECȚIE">DEZINSECȚIE</SelectItem>
                                  <SelectItem value="DEZINFECȚIE">DEZINFECȚIE</SelectItem>
                                  <SelectItem value="DERATIZARE">DERATIZARE</SelectItem>
                                </SelectContent>
                              </Select>
                              <Select value={s.stock_id || ""} onValueChange={(v) => pickFromStock(key, i, v)}>
                                <SelectTrigger className="h-9 text-sm col-span-2" data-testid={`sub-stock-picker-${key}-${i}`}>
                                  <SelectValue placeholder={stocksList.length ? "Alege din stoc..." : "Fără stocuri"} />
                                </SelectTrigger>
                                <SelectContent>
                                  {(() => {
                                    const rowSvc = (s.serviciu || "DEZINSECȚIE").toUpperCase();
                                    const filtered = stocksList.filter((st) => !st.servicii || st.servicii.length === 0 || st.servicii.includes(rowSvc));
                                    if (filtered.length === 0) {
                                      return <div className="px-2 py-3 text-xs text-muted-foreground">Nicio substanță marcată pentru {rowSvc}.</div>;
                                    }
                                    return filtered.map((st) => (
                                      <SelectItem key={st.id} value={st.id}>
                                        {st.denumire}{st.aviz_lot ? ` · Lot ${st.aviz_lot}` : ""} · {st.cantitate} {st.unitate}
                                      </SelectItem>
                                    ));
                                  })()}
                                </SelectContent>
                              </Select>
                            </div>
                            <Input value={s.denumire} onChange={(e) => updateSubstanta(key, i, "denumire", e.target.value)} placeholder="Denumire produs" className="h-9 text-sm" />
                            <Input value={s.substanta_activa} onChange={(e) => updateSubstanta(key, i, "substanta_activa", e.target.value)} placeholder="Substanță activă / compoziție" className="h-9 text-sm" />
                            <Input value={s.aviz} onChange={(e) => updateSubstanta(key, i, "aviz", e.target.value)} placeholder="Aviz / Autorizație" className="h-9 text-sm" />
                            <Input value={s.aviz_lot || ""} onChange={(e) => updateSubstanta(key, i, "aviz_lot", e.target.value)} placeholder="Aviz Lot (număr lot fabricație)" className="h-9 text-sm" />
                            <div>
                              <Label className="text-[10px] text-muted-foreground uppercase tracking-wider">Metoda de aplicare — {normalizeService(s.serviciu || "DEZINSECȚIE")}</Label>
                              <Select
                                value={s.mod_aplicare || ""}
                                onValueChange={(v) => updateSubstanta(key, i, "mod_aplicare", v)}
                              >
                                <SelectTrigger className="h-9 text-sm mt-1" data-testid={`sub-mod-aplicare-${key}-${i}`}>
                                  <SelectValue placeholder="— Alege metoda de aplicare —" />
                                </SelectTrigger>
                                <SelectContent>
                                  {(METODE_BY_SERVICE[normalizeService(s.serviciu || "DEZINSECȚIE")] || []).map((m) => (
                                    <SelectItem key={m} value={m}>{m}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              <Input value={s.consum_concentrata} onChange={(e) => updateSubstanta(key, i, "consum_concentrata", e.target.value)} placeholder="Consum concentrată" className="h-9 text-sm" />
                              <Input value={s.consum_total} onChange={(e) => updateSubstanta(key, i, "consum_total", e.target.value)} placeholder="Consum total soluție" className="h-9 text-sm" />
                              <Input value={s.timp_actiune} onChange={(e) => updateSubstanta(key, i, "timp_actiune", e.target.value)} placeholder="Timp acțiune" className="h-9 text-sm" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {!form.interior && !form.exterior && (
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center text-sm text-muted-foreground">
                  Bifează Interior sau Exterior mai sus pentru a completa serviciile, dăunătorii și substanțele.
                </div>
              )}

              <div>
                <Label className="label-eyebrow">Descrierea lucrărilor (opțional)</Label>
                <Textarea value={form.descriere} onChange={(e) => setForm({ ...form, descriere: e.target.value })} className="mt-2" rows={2} placeholder="Detalii specifice despre zone tratate, focare, etc." />
              </div>

              <div>
                <Label className="label-eyebrow">Recomandări post-intervenție (opțional)</Label>
                <Textarea value={form.recomandari} onChange={(e) => setForm({ ...form, recomandari: e.target.value })} className="mt-2" rows={3} placeholder="Dacă e gol, în PDF se folosesc recomandările standard oficiale." />
              </div>
            </>
          )}

          <div>
            <Label className="label-eyebrow">Observații</Label>
            <Textarea value={form.observatii} onChange={(e) => setForm({ ...form, observatii: e.target.value })} className="mt-2" rows={2} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Anulează</Button>
            {draftRestored && !isEdit && (
              <Button type="button" variant="ghost" onClick={clearDraft} className="text-xs text-slate-500" data-testid="clear-draft-btn">
                Șterge draft-ul
              </Button>
            )}
            <Button type="button" variant="outline" onClick={generatePreview} disabled={previewing} data-testid="preview-pv-btn">
              {previewing ? "Se generează..." : "Previzualizare PDF"}
            </Button>
            {(() => {
              const missing = [];
              if (!form.numar) missing.push("Număr");
              if (!form.data) missing.push("Data");
              const hasSvc = (zones.interior.servicii || []).length > 0 || (zones.exterior.servicii || []).length > 0;
              if (template === "ddd") {
                if (!form.locatie) missing.push("Locație");
                if (!hasSvc) missing.push("Servicii (Interior/Exterior)");
                if (!form.tehnician && !zones.interior.tehnician && !zones.exterior.tehnician) missing.push("Tehnician");
                const hasSub = (zones.interior.substante || []).length > 0 || (zones.exterior.substante || []).length > 0;
                if (hasSvc && !hasSub) missing.push("Cel puțin o substanță");
                if (!form.nr_reprize && !zones.interior.nr_reprize && !zones.exterior.nr_reprize) missing.push("Nr. reprize");
                if (!form.ora_start && !zones.interior.ora_start && !zones.exterior.ora_start) missing.push("Ora start");
                if (!form.durata_minute && !zones.interior.durata_minute && !zones.exterior.durata_minute) missing.push("Durată (min)");
                if (!form.tip_obiectiv) missing.push("Tipul obiectivului");
              } else {
                if (!form.descriere) missing.push("Descriere");
              }
              const invalid = missing.length > 0;
              return (
                <>
                  {invalid && (
                    <div className="mr-auto self-center bg-red-50 border border-red-300 rounded-md px-3 py-2 max-w-[70%]" data-testid="pv-missing-fields">
                      <p className="text-xs font-bold text-red-800 mb-0.5">⚠ Lipsesc {missing.length} câmp{missing.length > 1 ? "uri" : ""}:</p>
                      <ul className="text-xs text-red-700 grid grid-cols-2 gap-x-3">
                        {missing.map((m) => <li key={m}>• {m}</li>)}
                      </ul>
                    </div>
                  )}
                  <Button
                    type="submit"
                    disabled={saving || invalid}
                    className="btn-gold hover:btn-gold"
                    data-testid="save-pv-btn"
                  >
                    {saving ? "Se salvează..." : "Salvează PV"}
                  </Button>
                </>
              );
            })()}
          </DialogFooter>
        </form>
      </DialogContent>

      {/* PDF Preview modal */}
      <Dialog open={showPreviewPanel} onOpenChange={setShowPreviewPanel}>
        <DialogContent className="w-[95vw] max-w-5xl max-h-[85dvh] xl:max-h-[92dvh] overflow-y-auto" data-testid="pv-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Previzualizare PDF — PV</DialogTitle>
            <DialogDescription>Așa va arăta PV-ul generat. Închide și modifică dacă vrei ajustări.</DialogDescription>
          </DialogHeader>
          {previewUrl && (
            <iframe
              src={previewUrl}
              title="PV Preview"
              style={{ width: "100%", height: "78vh", border: "1px solid #E5E7EB", borderRadius: 6 }}
              data-testid="pv-preview-iframe"
            />
          )}
          <DialogFooter>
            {previewUrl && (
              <a href={previewUrl} target="_blank" rel="noreferrer" className="text-sm text-purple-700 hover:underline mr-auto">Deschide în tab nou</a>
            )}
            <Button type="button" variant="outline" onClick={() => setShowPreviewPanel(false)}>Închide</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}


// ─────────────────────────────────────────────────────────────
// AutoPVDialog — user selects (a) which months of the contract get an
// auto-generated PV and (b) which day of the month. The first manual PV
// of the contract is used as TEMPLATE (substances, zones, tehnician…).
// ─────────────────────────────────────────────────────────────
function AutoPVDialog({ contract, pvs, onClose, onDone }) {
  const open = !!contract;
  const contractDurata = Number(contract?.durata_luni || 0);
  const needsDurata = contractDurata <= 0;
  const [durata, setDurata] = useState(12);
  const monthsCount = Math.max(1, Math.min(24, needsDurata ? durata : contractDurata));

  // Base = luna în curs (nu data contractului). Luna 1 = luna curentă.
  const today = new Date();
  const base = new Date(today.getFullYear(), today.getMonth(), 1);

  // Manual PV template used as source
  const manualTemplate = pvs
    .filter((p) => p.contract_id === contract?.id && !p.auto_generated)
    .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""))[0];

  // Compute the list of {offset, ym: "YYYY-MM"} for the visible months
  const monthSlots = Array.from({ length: monthsCount }, (_, i) => {
    const d = new Date(base.getFullYear(), base.getMonth() + i, 1);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    return { offset: i + 1, ym, year: d.getFullYear(), month: d.getMonth() + 1 };
  });

  // Auto-PV months already existing for this contract → disable by "YYYY-MM"
  const existingAutoYMs = (() => {
    const set = new Set();
    pvs.filter((p) => p.contract_id === contract?.id && p.auto_generated && p.data).forEach((p) => {
      set.add(String(p.data).slice(0, 7));
    });
    return set;
  })();

  const [selected, setSelected] = useState(new Set()); // Set of "YYYY-MM"
  const [day, setDay] = useState(15);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      // Reset durata la default 12 dacă nu e setată pe contract
      if (contractDurata <= 0) setDurata(12);
      // Default: bifează toate lunile care nu au deja PV auto
      const s = new Set();
      const mc = contractDurata > 0 ? contractDurata : 12;
      for (let i = 0; i < mc; i++) {
        const d = new Date(base.getFullYear(), base.getMonth() + i, 1);
        const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        if (!existingAutoYMs.has(ym)) s.add(ym);
      }
      setSelected(s);
      setDay(15);
    }
    // eslint-disable-next-line
  }, [open, contract?.id]);

  // When user changes durata (only in `needsDurata` mode), re-bifează lunile noi
  useEffect(() => {
    if (!open || !needsDurata) return;
    const s = new Set();
    for (let i = 0; i < monthsCount; i++) {
      const d = new Date(base.getFullYear(), base.getMonth() + i, 1);
      const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (!existingAutoYMs.has(ym)) s.add(ym);
    }
    setSelected(s);
    // eslint-disable-next-line
  }, [durata]);

  const toggle = (ym) => {
    if (existingAutoYMs.has(ym)) return;
    setSelected((cur) => {
      const n = new Set(cur);
      if (n.has(ym)) n.delete(ym); else n.add(ym);
      return n;
    });
  };

  const selectAll = () => {
    const s = new Set();
    monthSlots.forEach(({ ym }) => { if (!existingAutoYMs.has(ym)) s.add(ym); });
    setSelected(s);
  };
  const clearAll = () => setSelected(new Set());

  const submit = async () => {
    if (selected.size === 0) {
      toast.error("Bifează măcar o lună");
      return;
    }
    if (needsDurata && (!durata || durata < 1)) {
      toast.error("Setează durata contractului (min. 1 lună)");
      return;
    }
    setSubmitting(true);
    try {
      // Convert selected "YYYY-MM" into absolute dates YYYY-MM-DD
      const dd = String(Math.max(1, Math.min(28, day))).padStart(2, "0");
      const dates = [...selected]
        .sort()
        .map((ym) => `${ym}-${dd}`);
      const payload = { dates, day };
      if (needsDurata) payload.durata_luni = Number(durata);
      const { data } = await api.post(`/contracts/${contract.id}/generate-pvs`, payload);
      toast.success(`${data.count} PV-uri generate. Nu scad din stoc.`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare generare");
    } finally {
      setSubmitting(false);
    }
  };

  if (!contract) return null;
  const RO = ["Ian", "Feb", "Mar", "Apr", "Mai", "Iun", "Iul", "Aug", "Sep", "Oct", "Noi", "Dec"];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[85dvh] xl:max-h-[92dvh] overflow-y-auto" data-testid="auto-pv-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Activity className="w-5 h-5 text-purple-600" />
            Auto PV — {contract.numar}
          </DialogTitle>
          <DialogDescription>
            Alege exact în ce luni să fie generate PV-urile. Primul PV manual servește drept șablon (substanțe, zone, tehnician se copiază identic).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Template status */}
          {manualTemplate ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm">
              <p className="font-semibold text-emerald-900">✓ Șablon detectat: PV {manualTemplate.numar}</p>
              <p className="text-emerald-800 text-xs mt-1">
                Servicii: {(manualTemplate.servicii || []).join(" • ") || "—"} · Substanțe: {(manualTemplate.substante || []).length}
              </p>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm">
              <p className="font-semibold text-amber-900">⚠ Nu există un PV manual pentru acest contract</p>
              <p className="text-amber-800 text-xs mt-1">
                Recomandat: închide și creează întâi <b>manual primul PV</b> pentru contract, apoi revino aici — PV-urile auto vor prelua toate detaliile din el. Poți continua fără șablon, dar PV-urile vor fi goale.
              </p>
            </div>
          )}

          {/* Durata setter (visible only when contract has no durata_luni) */}
          {needsDurata && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <Label className="label-eyebrow text-blue-900">Durata contract (luni) *</Label>
              <div className="flex items-center gap-3 mt-2">
                <Input
                  type="number" min="1" max="24"
                  value={durata}
                  onChange={(e) => setDurata(Math.max(1, Math.min(24, parseInt(e.target.value || "12"))))}
                  className="w-24 h-10 font-mono-crm"
                  data-testid="auto-pv-durata"
                />
                <p className="text-xs text-blue-800">
                  Contractul nu are durata setată. Alege câte luni durează pentru a genera PV-urile lunare.
                </p>
              </div>
            </div>
          )}

          {/* Day picker */}
          <div className="flex items-center gap-3">
            <Label className="label-eyebrow whitespace-nowrap">Ziua din lună</Label>
            <Input
              type="number" min="1" max="28"
              value={day}
              onChange={(e) => setDay(Math.max(1, Math.min(28, parseInt(e.target.value || "15"))))}
              className="w-24 h-10 font-mono-crm"
              data-testid="auto-pv-day"
            />
            <p className="text-xs text-muted-foreground">Toate PV-urile vor fi datate în această zi.</p>
          </div>

          {/* Month checkboxes */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="label-eyebrow">Luni selectate ({selected.size} din {monthsCount})</Label>
              <div className="flex gap-2">
                <button type="button" onClick={selectAll} className="text-xs text-purple-700 hover:underline" data-testid="auto-pv-select-all">Toate</button>
                <span className="text-xs text-muted-foreground">·</span>
                <button type="button" onClick={clearAll} className="text-xs text-slate-600 hover:underline" data-testid="auto-pv-clear-all">Nimic</button>
              </div>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-72 overflow-y-auto pr-1">
              {monthSlots.map(({ offset, ym, year, month }) => {
                const alreadyGenerated = existingAutoYMs.has(ym);
                const isSel = selected.has(ym);
                return (
                  <button
                    key={ym}
                    type="button"
                    onClick={() => toggle(ym)}
                    disabled={alreadyGenerated}
                    data-testid={`auto-pv-month-${offset}`}
                    className={`px-3 py-2 rounded-md border text-sm text-left transition-colors ${
                      alreadyGenerated
                        ? "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed"
                        : isSel
                          ? "bg-purple-600 border-purple-700 text-white"
                          : "bg-white border-slate-300 hover:border-purple-400"
                    }`}
                  >
                    <span className="text-[10px] uppercase tracking-widest opacity-70 block">Luna {offset}</span>
                    <span className="font-semibold">{RO[month - 1]} {year}</span>
                    {alreadyGenerated && <span className="block text-[10px] mt-0.5">deja generat</span>}
                  </button>
                );
              })}
            </div>
          </div>

          <p className="text-xs text-muted-foreground">
            PV-urile auto <b>nu scad</b> din stoc (ediți-le manual dacă vrei ca stocul să se descarce).
          </p>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} data-testid="auto-pv-cancel">Anulează</Button>
          <Button
            type="button"
            onClick={submit}
            disabled={submitting || selected.size === 0}
            className="btn-gold hover:btn-gold"
            data-testid="auto-pv-generate"
          >
            {submitting ? "Generez..." : `Generează ${selected.size} PV-uri`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
